import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSocket } from '../../context/SocketContext';
import { API_BASE_URL, API_BASE_PYTHON_SERVICE } from '../../utils/config';
import axios from 'axios';
import {
    polygon               as turfPolygon,
    area                  as turfArea,
    union                 as turfUnion,
    difference            as turfDifference,
    intersect             as turfIntersect,
    lineSplit             as turfLineSplit,
    lineString            as turfLineString,
    lineIntersect         as turfLineIntersect,
    length                as turfLength,
    cleanCoords           as turfCleanCoords,
    bearing               as turfBearing,
    destination           as turfDestination,
    unkinkPolygon         as turfUnkinkPolygon,
    buffer                as turfBuffer,
    nearestPointOnLine    as turfNearestPointOnLine,
    point                 as turfPoint,
    distance              as turfDistance,
} from '@turf/turf';



import BarIndicator from '../../components/BarIndicator/BarIndicator';
import { v4 as uuidv4 } from 'uuid';
import CommonMap from '../../components/CommonMap/CommonMap';
import MapDialog from '../../components/MapDialog/MapDialog';
import { Snackbar, Alert } from '@mui/material';
import { confirmarConsumoApsSiPendiente } from '../../utils/creditosAps';

const AerialApplications = ({
                                idAnalisis,
                                tipoAnalisis,
                                onAreasCalculated,
                                onPromediosCalculated,
                                activarEdicionInteractiva,
                                highlightedLote,
                                activeLotes,
                                polygonsData,
                                visibleLotePolygons,
                                loteFilters,
                                onSelectLote,
                                onLeaveLote,
                                onHoverLote,
                                isFilterDialogOpen,
                                closeFilterDialog,
                                setImgLaflet,
                                mapRef,
                                polygonsLayerRef,
                                setLabelsAndColors,
                                setOnAreasCalculatedLoader,
                                activeTool,
                                isBufferActive,
                                bufferValue,
                                showUnfilteredLines,
                                setActiveTool,
                                setTipoMapeo,
                                setNorthWestCoords,
                                setSouthEastCoords,
                                setCoordinateSystem,
                                registerUndoHandler
                            }) => {
    const analisisId = idAnalisis?.data?.ID_ANALISIS || null;

    // Herbicidas comparte esta vista con Aplicaciones Aéreas. Sus claves de
    // almacenamiento, eventos y endpoints son propios; APS no cambia.
    const isHerbicidasAnalysis = tipoAnalisis === 'HERBICIDAS';
    const vraModeStorageKey = isHerbicidasAnalysis ? 'herbicidas_modo' : 'aplicacionesAereas_modo';
    const vraModeChangedEvent = isHerbicidasAnalysis ? 'herbicidas:modo-actualizado' : 'aplicacionesAereas:modo-actualizado';
    const tasaVariableSummaryEndpoint = isHerbicidasAnalysis
        ? 'dashboard/herbicidas/tasaVariable/completo'
        : 'dashboard/tasaVariable/completo';

    const userData = JSON.parse(localStorage.getItem('userData')) || {};

    const [polygons, setPolygons] = useState([]);
    const [varPoints, setVarPoints] = useState([]);

    // El lote proviene de la plataforma y no del ZIP objetivo de maleza.
    // Durante la actualización remota VAR algunas capas pueden refrescarse
    // temporalmente; conservar la última colección válida evita que desaparezca
    // del mapa y mantiene disponible su área como respaldo visual.
    const lastVisibleLotePolygonsRef = useRef(null);
    const stableVisibleLotePolygons = useMemo(() => {
        const hasVisibleFeatures =
            visibleLotePolygons?.type === 'FeatureCollection' &&
            Array.isArray(visibleLotePolygons.features) &&
            visibleLotePolygons.features.length > 0;

        if (hasVisibleFeatures) {
            lastVisibleLotePolygonsRef.current = visibleLotePolygons;
            return visibleLotePolygons;
        }

        return lastVisibleLotePolygonsRef.current || {
            type: 'FeatureCollection',
            features: [],
        };
    }, [visibleLotePolygons]);
    const [isTasaVariableMap, setIsTasaVariableMap] = useState(false);
    // La presentación visual VRA debe activarse desde que el usuario selecciona
    // el modo, antes de que terminen de llegar las capas remotas. Esto evita que
    // el lote amarillo se vea sólido durante algunos segundos y confunda al usuario.
    const [isVraModeSelected, setIsVraModeSelected] = useState(() =>
        String(localStorage.getItem(vraModeStorageKey) || 'CONVENCIONAL').toUpperCase() === 'VAR'
    );
    const [tasaVariableBounds, setTasaVariableBounds] = useState(null);
    // En VAR las capas grandes se entregan como URLs directas a Mapbox. Evita
    // copiar miles de coordenadas del worker hacia React y reconstruir GeoJSON.
    const [tasaVariableRemoteLayers, setTasaVariableRemoteLayers] = useState(null);
    const [areasSuperpuestas, setAreasSuperpuestas] = useState([]);
    const [mapCenter] = useState([0, 0]);
    const [zoom] = useState(3);
    const [activeFilter, setActiveFilter] = useState(null);
    const [polygonsProperties, setPolygonsProperties] = useState([]);
    const [lines, setLines] = useState([]);
    const [lineasNoFiltradas, setLineasNoFiltradas] = useState([]);
    const [bufferedLines, setBufferedLines] = useState([]);

    // Buffer recortado (según filtros/selección de lotes) reportado por CommonMap.
    // Esto se usa para el cálculo de áreas, para NO mandar siempre el buffer completo al backend.
    const [clippedBufferedLinesFC, setClippedBufferedLinesFC] = useState({ type: 'FeatureCollection', features: [] });
    const clippedBufferReadyRef = useRef(false);

    const handleClippedBufferChange = useCallback((fc) => {
        clippedBufferReadyRef.current = true;

        if (fc?.type === 'FeatureCollection' && Array.isArray(fc.features)) {
            setClippedBufferedLinesFC(fc);
            return;
        }
        if (Array.isArray(fc)) {
            setClippedBufferedLinesFC({ type: 'FeatureCollection', features: fc });
            return;
        }
        setClippedBufferedLinesFC({ type: 'FeatureCollection', features: [] });
    }, []);
    const [bufferedIntersections, setBufferedIntersections] = useState([]);
    const [kmlPolygons, setKmlPolygons] = useState([]);
    const [isKml, setIsKml] = useState(false);
    const [popupInfo, setPopupInfo] = useState(null);
    const [isFilteringLines, setIsFilteringLines] = useState(true);
    const [actionHistory, setActionHistory] = useState([]);
    const [areasCalculated, setAreasCalculated] = useState(false);

    const socketContext = useSocket();
    const { socket, socketSessionID } = socketContext || {};

    // Si hay filtros de lote aplicados desde el FilterToolbar, usamos esos polígonos como "clipPolygonsFC"
    // para forzar el recorte (spatial filter) en CommonMap aunque no haya selección activa por click.
    const hasToolbarLoteFilters = useMemo(() => {
        const f = loteFilters || {};
        return ['loteId', 'finca', 'codigoFinca', 'lote'].some((k) => {
            const v = f[k];
            return Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '';
        });
    }, [loteFilters]);

    const [availableFilters, setAvailableFilters] = useState({
        speed: false,
        altitude: false,
        realDose: false,
    });
    const [filterSpeed, setFilterSpeed] = useState(false);
    const [filterAltitude, setFilterAltitude] = useState(false);
    const [filterRealDose, setFilterRealDose] = useState(false);

    const [lowSpeed, setLowSpeed] = useState(0);
    const [medSpeed, setMedSpeed] = useState(0);
    const [highSpeed, setHighSpeed] = useState(0);

    const [lowAltitude, setLowAltitude] = useState(0);
    const [medAltitude, setMedAltitude] = useState(0);
    const [highAltitude, setHighAltitude] = useState(0);

    const [lowRealDose, setLowRealDose] = useState(0);
    const [medRealDose, setMedRealDose] = useState(0);
    const [highRealDose, setHighRealDose] = useState(0);

    const [nonIntersectedAreas, setNonIntersectedAreas] = useState([]);

    // Dibujo
    const isDrawingRef = useRef(false);
    const newLineCoordsRef = useRef([]);
    // Corte
    const isCuttingRef = useRef(false);
    const cutLineCoordsRef = useRef([]);
    // Medición
    const isMeasuringRef = useRef(false);
    const measureCoordsRef = useRef([]);

    const workerRef = useRef(null);
    const activeToolRef = useRef(activeTool);

    const linesRef = useRef(lines);
    const unfilteredLinesRef = useRef(lineasNoFiltradas);

    useEffect(() => {
        linesRef.current = lines;
    }, [lines]);

    useEffect(() => {
        unfilteredLinesRef.current = lineasNoFiltradas;
    }, [lineasNoFiltradas]);

    useEffect(() => {
        const syncVraMode = () => {
            setIsVraModeSelected(
                String(localStorage.getItem(vraModeStorageKey) || 'CONVENCIONAL').toUpperCase() === 'VAR'
            );
        };

        syncVraMode();
        window.addEventListener('storage', syncVraMode);
        window.addEventListener(vraModeChangedEvent, syncVraMode);
        return () => {
            window.removeEventListener('storage', syncVraMode);
            window.removeEventListener(vraModeChangedEvent, syncVraMode);
        };
    }, [vraModeStorageKey, vraModeChangedEvent]);

// Timeout independiente para cada tipo de cálculo
    const noBufferTimeoutRef = useRef(null);   // cálculo normal (sin buffer)
    const bufferTimeoutRef   = useRef(null);   // cálculo con buffer

// Control de peticiones de cálculo con buffer
    const bufferRequestAbortRef = useRef(null);
    const bufferRequestIdRef    = useRef(0);

// Control independiente para el recálculo VAR disparado desde el buffer del frontend.
// VAR no utiliza calculate_areas legacy: el servicio Python reconstruye cobertura,
// métricas, líneas verdes/rojas y maleza no aplicada a partir de las capas fuente.
    const varBufferTimeoutRef = useRef(null);
    const varBufferRequestAbortRef = useRef(null);
    const prevVarBufferHashRef = useRef('');
    const latestVarBufferRadiusRef = useRef(null);

// Anti-bucle para cálculos con buffer
    const prevBufferCalcHashRef = useRef('');

// Anti-bucle para cálculos sin buffer (polígonos)
    const prevPolygonsHashRef   = useRef('');

// Unmount cleanup for pending buffer calculations (timers + in-flight request)
    useEffect(() => {
        return () => {
            try {
                if (bufferTimeoutRef.current) {
                    clearTimeout(bufferTimeoutRef.current);
                    bufferTimeoutRef.current = null;
                }
            } catch (e) { /* noop */ }

            try {
                if (noBufferTimeoutRef.current) {
                    clearTimeout(noBufferTimeoutRef.current);
                    noBufferTimeoutRef.current = null;
                }
            } catch (e) { /* noop */ }

            try {
                if (bufferRequestAbortRef.current) {
                    bufferRequestAbortRef.current.abort();
                    bufferRequestAbortRef.current = null;
                }
            } catch (e) { /* noop */ }

            try {
                if (varBufferTimeoutRef.current) {
                    clearTimeout(varBufferTimeoutRef.current);
                    varBufferTimeoutRef.current = null;
                }
            } catch (e) { /* noop */ }

            try {
                if (varBufferRequestAbortRef.current) {
                    varBufferRequestAbortRef.current.abort();
                    varBufferRequestAbortRef.current = null;
                }
            } catch (e) { /* noop */ }
        };
    }, []);


    // Siempre usamos ref para las callbacks, así no entran en effects pesados como dependencia
    const onAreasCalculatedRef = useRef(onAreasCalculated);
    const onPromediosCalculatedRef = useRef(onPromediosCalculated);
    const tasaVariableSummaryAppliedRef = useRef('');
    const lastTasaVariableSummaryRef = useRef(null);

    useEffect(() => {
        onAreasCalculatedRef.current = onAreasCalculated;
    }, [onAreasCalculated]);

    useEffect(() => {
        onPromediosCalculatedRef.current = onPromediosCalculated;
    }, [onPromediosCalculated]);

    // El ZIP objetivo de VAR representa únicamente maleza. El área del lote
    // debe obtenerse de los polígonos visibles de la plataforma, no de esa capa.
    const areaLoteVisibleHa = useMemo(() => {
        try {
            if (!stableVisibleLotePolygons) return 0;
            const areaM2 = turfArea(stableVisibleLotePolygons);
            return Number.isFinite(areaM2) && areaM2 > 0 ? areaM2 / 10000 : 0;
        } catch (error) {
            console.warn('[VAR indicadores] No se pudo calcular el área visible del lote:', error?.message || error);
            return 0;
        }
    }, [stableVisibleLotePolygons]);

    const unwrapVarMetricValue = useCallback((value) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            if (Object.prototype.hasOwnProperty.call(value, 'valor')) return value.valor;
            if (Object.prototype.hasOwnProperty.call(value, 'value')) return value.value;
            if (Object.prototype.hasOwnProperty.call(value, 'raw')) return value.raw;
        }
        return value;
    }, []);

    const toVarNumber = useCallback((value, fallback = 0) => {
        const raw = unwrapVarMetricValue(value);
        if (typeof raw === 'number') return Number.isFinite(raw) ? raw : fallback;
        if (raw === null || typeof raw === 'undefined') return fallback;
        const normalized = String(raw)
            .replace(/,/g, '.')
            .replace(/[^0-9.\-]/g, '');
        const parsed = Number.parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : fallback;
    }, [unwrapVarMetricValue]);

    const pickVarMetric = useCallback((source, keys, fallback = 0) => {
        const root = source?.summary && typeof source.summary === 'object' ? source.summary : source;
        for (const key of keys) {
            if (!root || !Object.prototype.hasOwnProperty.call(root, key)) continue;
            const raw = unwrapVarMetricValue(root[key]);
            if (raw === undefined || raw === null || String(raw).trim() === '') continue;
            const parsed = toVarNumber(raw, Number.NaN);
            if (Number.isFinite(parsed)) return parsed;
        }
        return fallback;
    }, [toVarNumber, unwrapVarMetricValue]);

    const normalizeTasaVariableSummary = useCallback((summaryLike) => {
        const root = summaryLike?.summary && typeof summaryLike.summary === 'object'
            ? summaryLike.summary
            : summaryLike;

        if (!root || typeof root !== 'object') return null;

        // En VAR la capa objetivo representa maleza, no traslape ni lote completo.
        // Se conservan alias anteriores para procesar análisis históricos.
        const areaAplicadaEnMaleza = pickVarMetric(root, [
            'AREA_APLICADA_EN_MALEZA_HA', 'areaAplicadaEnMalezaHa', 'area_aplicada_en_maleza_ha',
            'AREA_APLICADA_OBJETIVO_HA', 'areaAplicadaObjetivoHa', 'area_aplicada_objetivo_ha',
            'AREA_APLICADA', 'areaAplicada'
        ]);
        const areaAplicadaFueraMaleza = pickVarMetric(root, [
            'AREA_APLICADA_FUERA_MALEZA_HA', 'areaAplicadaFueraMalezaHa', 'area_aplicada_fuera_maleza_ha',
            'AREA_APLICADA_FUERA_OBJETIVO_HA', 'areaAplicadaFueraObjetivoHa', 'area_aplicada_fuera_objetivo_ha',
            'AREA_SOBRE_APLICADA', 'areaSobreAplicada'
        ]);
        const areaMalezaNoAplicadaReportada = pickVarMetric(root, [
            'AREA_MALEZA_NO_APLICADA_HA', 'areaMalezaNoAplicadaHa', 'area_maleza_no_aplicada_ha',
            'AREA_OBJETIVO_NO_APLICADA_HA', 'areaObjetivoNoAplicadaHa', 'area_objetivo_no_aplicada_ha',
            'AREA_NO_APLICADA', 'areaNoAplicada'
        ]);
        const areaMalezaTotal = pickVarMetric(root, [
            'AREA_MALEZA_HA', 'areaMalezaHa', 'area_maleza_ha',
            'AREA_OBJETIVO_HA', 'areaObjetivoHa', 'area_objetivo_ha'
        ], areaAplicadaEnMaleza + areaMalezaNoAplicadaReportada);
        // Regla VAR solicitada: maleza no aplicada = maleza total - aplicada en maleza.
        const areaMalezaNoAplicada = Math.max(areaMalezaTotal - areaAplicadaEnMaleza, 0);
        const areaLoteBackend = pickVarMetric(root, [
            'AREA_LOTE_HA', 'areaLoteHa', 'area_lote_ha',
            'AREA_TOTAL_LOTE_HA', 'areaTotalLoteHa', 'area_total_lote_ha'
        ]);
        const areaLoteHa = areaLoteBackend > 0 ? areaLoteBackend : areaLoteVisibleHa;
        const areaAplicadaTotal = pickVarMetric(root, [
            'AREA_APLICADA_TOTAL_HA', 'areaAplicadaTotalHa', 'area_aplicada_total_ha'
        ], areaAplicadaEnMaleza + areaAplicadaFueraMaleza);

        const hasAnyArea = [areaAplicadaEnMaleza, areaAplicadaFueraMaleza, areaMalezaNoAplicada, areaMalezaTotal, areaAplicadaTotal]
            .some((value) => Number.isFinite(value) && value > 0);
        if (!hasAnyArea) return null;

        return {
            // Alias heredados consumidos por el dashboard general.
            areaSobreAplicada: areaAplicadaFueraMaleza,
            areaAplicada: areaAplicadaEnMaleza,
            areaNoAplicada: areaMalezaNoAplicada,
            areaTotalLote: areaLoteHa,
            areaAplicadaEnCalles: 0,
            areaAplicadaFueraDelLote: areaAplicadaFueraMaleza,
            // Nombres explícitos para componentes VAR.
            areaLoteHa,
            areaMalezaHa: areaMalezaTotal,
            areaAplicadaEnMalezaHa: areaAplicadaEnMaleza,
            areaAplicadaFueraMalezaHa: areaAplicadaFueraMaleza,
            areaMalezaNoAplicadaHa: areaMalezaNoAplicada,
            coberturaMalezaPct: pickVarMetric(root, ['COBERTURA_MALEZA_PCT', 'coberturaMalezaPct', 'CUMPLIMIENTO_PCT', 'cumplimientoPct']),
            aplicacionFueraMalezaPct: pickVarMetric(root, ['APLICACION_FUERA_MALEZA_PCT', 'aplicacionFueraMalezaPct', 'FUERA_OBJETIVO_PCT', 'fueraObjetivoPct']),
            porcentajeDeVariacion: pickVarMetric(root, ['MALEZA_NO_APLICADA_PCT', 'malezaNoAplicadaPct', 'NO_APLICADO_PCT', 'noAplicadoPct', 'no_aplicado_pct']),
            porcentajeVariacionDosis: pickVarMetric(root, [
                'PORCENTAJE_VOLUMEN_REAL_VS_TEORICO', 'porcentajeVolumenRealVsTeorico',
                'PORCENTAJE_VARIACION_DOSIS', 'porcentajeVariacionDosis'
            ], (() => {
                const litrosReales = pickVarMetric(root, ['LITROS_APLICADOS_TOTAL', 'litrosAplicadosTotal'], Number.NaN);
                const litrosTeoricos = pickVarMetric(root, ['LITROS_TEORICOS_TOTAL', 'litrosTeoricosTotal'], Number.NaN);
                if (Number.isFinite(litrosReales) && Number.isFinite(litrosTeoricos) && litrosTeoricos > 0) {
                    return Number(((litrosReales / litrosTeoricos) * 100).toFixed(2));
                }
                const dosisRealPromedio = pickVarMetric(root, ['DOSIS_REAL_PROMEDIO', 'dosisRealPromedio'], Number.NaN);
                const dosisObjetivoPromedio = pickVarMetric(root, ['DOSIS_OBJETIVO_PROMEDIO', 'dosisObjetivoPromedio'], Number.NaN);
                return Number.isFinite(dosisRealPromedio) && Number.isFinite(dosisObjetivoPromedio) && dosisObjetivoPromedio > 0
                    ? Number(((dosisRealPromedio / dosisObjetivoPromedio) * 100).toFixed(2))
                    : 0;
            })()),
            dosisRealPromedio: pickVarMetric(root, ['DOSIS_REAL_PROMEDIO', 'dosisRealPromedio', 'dosis_real_promedio']),
            dosisPrescripcionPromedio: pickVarMetric(root, [
                'DOSIS_PRESCRIPCION_PROMEDIO', 'dosisPrescripcionPromedio',
                'DOSIS_TEORICA', 'dosisTeorica',
                'DOSIS_OBJETIVO_PROMEDIO', 'dosisObjetivoPromedio'
            ]),
            dosisTotalDiferencia: pickVarMetric(root, ['DIFERENCIA_DOSIS', 'diferenciaDosis', 'diferencia_dosis']),
            dosisTotalRealAplicada: pickVarMetric(root, [
                'LITROS_APLICADOS_TOTAL', 'litrosAplicadosTotal',
                'DOSIS_TOTAL_REAL_APLICADA', 'dosisTotalRealAplicada',
                'DOSIS_REAL_PROMEDIO', 'dosisRealPromedio', 'dosis_real_promedio'
            ]),
            litrosTeoricosTotal: pickVarMetric(root, ['LITROS_TEORICOS_TOTAL', 'litrosTeoricosTotal']),
            distanciaTotalM: pickVarMetric(root, ['DISTANCIA_TOTAL_M', 'distanciaTotalM']),
            fuelTotal: pickVarMetric(root, ['FUEL_TOTAL', 'fuelTotal']),
            areaRecorridaHa: pickVarMetric(root, ['AREA_RECORRIDA_HA', 'areaRecorridaHa']),
            alturas: root.ALTURAS || root.alturas || [],
            dosisReales: root.DOSIS_REALES || root.dosisReales || [],
            dosisAreas: root.DOSIS_AREAS || root.dosisAreas || [],
            numeroKmls: pickVarMetric(root, ['NUMERO_KMLS', 'numeroKmls']),
            promedioVelocidad: pickVarMetric(root, ['VELOCIDAD_PROMEDIO', 'velocidadPromedio', 'velocidad_promedio']),
            promedioAltura: pickVarMetric(root, ['ALTURA_PROMEDIO', 'alturaPromedio', 'altura_promedio']),
            metadata: root.METADATA || root.metadata || null,
            operador: root.NOMBRE_DE_OPERADOR || root.operador || root.METADATA?.OPERADOR || root.metadata?.OPERADOR,
            serialMaquinaria: root.SERIAL_MAQUINARIA || root.serialMaquinaria || root.METADATA?.SERIAL_MAQUINARIA || root.metadata?.SERIAL_MAQUINARIA,
            finca: root.NOMBRE_FINCA || root.finca || root.METADATA?.FINCA || root.metadata?.FINCA,
            campo: root.CAMPO || root.campo || root.METADATA?.CAMPO || root.metadata?.CAMPO,
            operacion: root.OPERACION || root.operacion || root.METADATA?.OPERACION || root.metadata?.OPERACION,
        };
    }, [areaLoteVisibleHa, pickVarMetric]);

    const applyTasaVariableSummary = useCallback((summaryLike, source = 'socket') => {
        if (summaryLike && typeof summaryLike === 'object') {
            lastTasaVariableSummaryRef.current = summaryLike;
        }
        const normalized = normalizeTasaVariableSummary(summaryLike);
        if (!normalized) return false;

        const cacheKey = JSON.stringify({ analisisId, ...normalized });
        if (tasaVariableSummaryAppliedRef.current === cacheKey) {
            return true;
        }
        tasaVariableSummaryAppliedRef.current = cacheKey;

        if (typeof onAreasCalculatedRef.current === 'function') {
            onAreasCalculatedRef.current(normalized);
        }

        if (typeof onPromediosCalculatedRef.current === 'function') {
            onPromediosCalculatedRef.current({
                promedioVelocidad: normalized.promedioVelocidad,
                promedioAltura: normalized.promedioAltura,
            });
        }

        if (typeof setOnAreasCalculatedLoader === 'function') {
            setOnAreasCalculatedLoader(true);
        }

        console.info('[VAR indicadores] métricas aplicadas', { source, ...normalized });
        return true;
    }, [analisisId, normalizeTasaVariableSummary, setOnAreasCalculatedLoader]);

    const fetchAndApplyTasaVariableSummary = useCallback(async (summaryFromSocket = null) => {
        if (applyTasaVariableSummary(summaryFromSocket, 'socket')) {
            return;
        }

        if (!analisisId) return;

        try {
            const response = await axios.get(`${API_BASE_URL}${tasaVariableSummaryEndpoint}/${analisisId}`);
            applyTasaVariableSummary(response?.data, 'backend');
        } catch (error) {
            console.warn('[VAR indicadores] No se pudieron cargar métricas desde backend:', error?.message || error);
        }
    }, [analisisId, applyTasaVariableSummary, tasaVariableSummaryEndpoint]);

    // Si la capa del lote termina de cargarse después que el resumen VAR,
    // recalcular el KPI con el área visible estable. El cacheKey incluye el área,
    // por lo que solo se actualiza cuando realmente cambia.
    useEffect(() => {
        if (!isTasaVariableMap || !lastTasaVariableSummaryRef.current) return;
        applyTasaVariableSummary(lastTasaVariableSummaryRef.current, 'lote-visible-refresh');
    }, [areaLoteVisibleHa, applyTasaVariableSummary, isTasaVariableMap]);

    const nextLineIdRef = useRef(0);

    const currentToolCleanupRef = useRef(null);


    // La aplicación VRA llega como franjas en polígonos o como puntos unidos en
    // líneas, en cualquier análisis y equipo. La leyenda y el recálculo de
    // buffer dependen solo de esa geometría.
    const isPolygonTasaVariable = Boolean(
        isTasaVariableMap &&
        String(tasaVariableRemoteLayers?.geometriaAplicacion || tasaVariableRemoteLayers?.geometria_aplicacion || '')
            .toUpperCase() === 'POLYGONS'
    );

    useEffect(() => {
        let next;
        if (filterSpeed)       next = 'speed';
        else if (filterAltitude) next = 'altura';
        else if (filterRealDose) next = 'dosisReal';
        else if (isTasaVariableMap) next = isPolygonTasaVariable
                ? 'aerialApplicationsMalezaVarFranjas'
                : 'aerialApplicationsMalezaVar';
        else                    next = (Array.isArray(lines) && lines.length > 0)
                ? 'aerialApplicationsTraslapeDrones'
                : 'aerialApplicationsTraslape';
        setActiveFilter(next);
    }, [filterSpeed, filterAltitude, filterRealDose, lines, isTasaVariableMap, isPolygonTasaVariable]);


    useEffect(() => {
        onAreasCalculatedRef.current = onAreasCalculated;
    }, [onAreasCalculated]);

    const safeMapCall = useCallback((callback) => {
        const map = mapRef?.current;
        if (map && typeof map.isStyleLoaded === 'function' && map.isStyleLoaded()) {
            try {
                callback(map);
            } catch (error) {
                console.error('Error in map operation:', error);
            }
        }
    }, [mapRef]);

    const removeDrawingLayer = useCallback(() => {
        if (mapRef.current) {
            const map = mapRef.current;
            if (map.getLayer('drawing-line-layer')) {
                map.removeLayer('drawing-line-layer');
            }
            if (map.getSource('drawing-line')) {
                map.removeSource('drawing-line');
            }
        }
    }, [mapRef]);

    const removeCuttingLayer = useCallback(() => {
        if (mapRef.current) {
            const map = mapRef.current;
            if (map.getLayer('cutting-line-layer')) {
                map.removeLayer('cutting-line-layer');
            }
            if (map.getSource('cutting-line')) {
                map.removeSource('cutting-line');
            }
        }
    }, [mapRef]);

    // --- Medición: helpers de formato ---
    const formatDistance = (meters) => {
        if (!Number.isFinite(meters)) return '';
        if (meters < 1000) return `${meters.toFixed(1)} m`;
        return `${(meters / 1000).toFixed(2)} km`;
    };
    const formatArea = (m2) => {
        if (!Number.isFinite(m2)) return '';
        return `${(m2 / 10000).toFixed(2)} ha`;
    };

    // --- Medición: limpiar capas
    const removeMeasurementLayers = useCallback(() => {
        const map = mapRef.current;
        if (!map) return;
        try {
            ['measurement-line-layer','measurement-point-layer','measurement-label-layer']
                .forEach(id => { if (map.getLayer(id)) map.removeLayer(id); });
            ['measurement-line','measurement-point','measurement-label']
                .forEach(id => { if (map.getSource(id)) map.removeSource(id); });
        } catch (e) {
            // noop
        }
    }, [mapRef]);

    // --- Medición: actualizar fuentes/capas con coords actuales
    const updateMeasurementLayers = useCallback((coords) => {
        safeMapCall((m) => {
            // Crear fuentes si no existen
            if (!m.getSource('measurement-line')) {
                m.addSource('measurement-line', {
                    type: 'geojson',
                    data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords } }
                });
            } else {
                m.getSource('measurement-line').setData({
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates: coords }
                });
            }
            if (!m.getLayer('measurement-line-layer')) {
                m.addLayer({
                    id: 'measurement-line-layer',
                    type: 'line',
                    source: 'measurement-line',
                    paint: {
                        'line-color': '#111',
                        'line-width': 2,
                        'line-dasharray': [2, 2]
                    }
                });
            }

            // puntos (último punto)
            const last = coords[coords.length - 1] || null;
            const pointsFC = {
                type: 'FeatureCollection',
                features: last ? [{
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: last },
                    properties: {}
                }] : []
            };
            if (!m.getSource('measurement-point')) {
                m.addSource('measurement-point', { type: 'geojson', data: pointsFC });
            } else {
                m.getSource('measurement-point').setData(pointsFC);
            }
            if (!m.getLayer('measurement-point-layer')) {
                m.addLayer({
                    id: 'measurement-point-layer',
                    type: 'circle',
                    source: 'measurement-point',
                    paint: {
                        'circle-radius': 4,
                        'circle-color': '#1976d2',
                        'circle-stroke-width': 1,
                        'circle-stroke-color': '#fff'
                    }
                });
            }

            // Etiqueta
            let label = '';
            if (coords.length >= 2) {
                const ls = turfLineString(coords);
                const km = turfLength(ls, { units: 'kilometers' });
                const meters = km * 1000;
                label = `Distancia: ${formatDistance(meters)}`;
                if (coords.length >= 3) {
                    const ring = [...coords, coords[0]];
                    const a = turfArea(turfPolygon([ring]));
                    if (a > 0) {
                        label += ` • Área: ${formatArea(a)}`;
                    }
                }
            }
            const labelFC = {
                type: 'FeatureCollection',
                features: last && label
                    ? [{
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: last },
                        properties: { label }
                    }]
                    : []
            };
            if (!m.getSource('measurement-label')) {
                m.addSource('measurement-label', { type: 'geojson', data: labelFC });
            } else {
                m.getSource('measurement-label').setData(labelFC);
            }
            if (!m.getLayer('measurement-label-layer')) {
                m.addLayer({
                    id: 'measurement-label-layer',
                    type: 'symbol',
                    source: 'measurement-label',
                    layout: {
                        'text-field': ['get', 'label'],
                        'text-size': 12,
                        'text-anchor': 'top',
                        'text-offset': [0, -1.2]
                    },
                    paint: {
                        'text-halo-width': 1.2,
                        'text-halo-color': '#fff',
                        'text-color': '#111'
                    }
                });
            }
        });
    }, [safeMapCall]);


    const applyFilters = useCallback(() => {
        setPolygonsProperties((prevProps) =>
            prevProps.map((prop, index) => {
                if (!prop) return prop;
                let color = 'green';

                const speed = prop.VELOCIDAD;
                const altitude = prop.ALTURA;
                const realDose = prop.DOSISREAL;

                if (filterSpeed && typeof speed === 'number') {
                    if (speed < lowSpeed) color = 'green';
                    else if (speed >= lowSpeed && speed < medSpeed) color = 'yellow';
                    else if (speed >= medSpeed && speed <= highSpeed) color = 'orange';
                    else if (speed > highSpeed) color = 'red';
                }

                if (filterAltitude && typeof altitude === 'number') {
                    if (altitude < lowAltitude) color = 'green';
                    else if (altitude >= lowAltitude && altitude < medAltitude) color = 'yellow';
                    else if (altitude >= medAltitude && altitude <= highAltitude) color = 'orange';
                    else if (altitude > highAltitude) color = 'red';
                }

                if (filterRealDose && typeof realDose === 'number') {
                    if (realDose < lowRealDose) color = 'green';
                    else if (realDose >= lowRealDose && realDose < medRealDose) color = 'yellow';
                    else if (realDose >= medRealDose && realDose <= highRealDose) color = 'orange';
                    else if (realDose > highRealDose) color = 'red';
                }

                return { ...prop, color };
            })
        );
    }, [
        filterSpeed, filterAltitude, filterRealDose,
        lowSpeed, medSpeed, highSpeed,
        lowAltitude, medAltitude, highAltitude,
        lowRealDose, medRealDose, highRealDose
    ]);

    const formatPolygon = useCallback((polygon) => {
        if (!Array.isArray(polygon) || polygon.length < 3) return [];
        const first = polygon[0];
        const last = polygon[polygon.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
            polygon.push([first[0], first[1]]);
        }
        const formatted = polygon.map((coordPair) => {
            if (Array.isArray(coordPair) && coordPair.length >= 2) {
                return [coordPair[0], coordPair[1]];
            }
            return [];
        });
        return formatted;
    }, []);

    const fixCoordinate = ([a, b]) => {
        if (typeof a !== 'number' || typeof b !== 'number') {
            return null;
        }
        const latOk = a >= -90 && a <= 90;
        const lngOk = b >= -180 && b <= 180;
        if (latOk && lngOk) {
            return [a, b];
        }
        const latOk2 = b >= -90 && b <= 90;
        const lngOk2 = a >= -180 && a <= 180;
        if (latOk2 && lngOk2) {
            return [b, a];
        }
        return null;
    };

    const closeRing = (coords) => {
        if (!Array.isArray(coords) || coords.length === 0) return [];
        const first = coords[0];
        const last = coords[coords.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
            coords.push([...first]);
        }
        while (coords.length < 4) {
            coords.push([...coords[0]]);
        }
        return coords;
    };

    const normalizeRing = (ring) => {
        const fixed = ring.map(fixCoordinate).filter(Boolean);
        const closed = closeRing(fixed);
        return closed.length >= 4 ? closed : [];
    };

    useEffect(() => {
        workerRef.current = new Worker('Workers/dataWorker.js');

        workerRef.current.onmessage = (e) => {
            if (e.data.action === 'geoJsonDataProcessed') {
                const data = e.data.data || {};

                if (data.tasaVariable) {
                    const remoteLayers = data.remoteLayers && typeof data.remoteLayers === 'object'
                        ? data.remoteLayers
                        : null;
                    const hasRemoteLayers = Boolean(
                        remoteLayers && (
                            remoteLayers.lineasAplicacion ||
                            remoteLayers.lineasAplicadasEnMaleza ||
                            remoteLayers.lineasAplicadasFueraMaleza ||
                            remoteLayers.malezaObjetivo ||
                            remoteLayers.malezaNoAplicada
                        )
                    );

                    if (data.renderMode === 'remote' && hasRemoteLayers) {
                        setTasaVariableRemoteLayers(remoteLayers);
                        // Las capas se pintan por URL directamente en Mapbox.
                        // Mantener estados locales vacíos evita la doble carga y
                        // las transformaciones costosas en el hilo principal.
                        setLines([]);
                        setLineasNoFiltradas([]);
                        setBufferedLines([]);
                        setBufferedIntersections([]);
                        setKmlPolygons([]);
                        setPolygons([]);
                        setPolygonsProperties([]);
                        setVarPoints([]);
                        setIsTasaVariableMap(true);
                        setTasaVariableBounds(Array.isArray(data.bounds) ? data.bounds : null);
                        setIsKml(true);
                        setIsFilteringLines(false);
                        setActionHistory([]);
                        setAreasCalculated(true);

                        if (typeof setOnAreasCalculatedLoader === 'function') {
                            setOnAreasCalculatedLoader(true);
                        }

                        fetchAndApplyTasaVariableSummary(data.summary || data.tasaVariableSummary || data.varSummary || null);

                        console.info('[VAR map] fuentes remotas listas para Mapbox', {
                            layers: Object.keys(remoteLayers).filter((key) => Boolean(remoteLayers[key])),
                            bounds: data.bounds || null,
                        });
                        return;
                    }

                    setTasaVariableRemoteLayers(null);
                    const varPolygons = Array.isArray(data.polygons) ? data.polygons : [];
                    const varPolygonProperties = Array.isArray(data.polygonProperties) ? data.polygonProperties : [];
                    const varPointFeatures = Array.isArray(data.points) ? data.points : [];
                    const varLines = Array.isArray(data.lines) ? data.lines : [];
                    const createVarLatLngArray = (paths) => {
                        const latLngArray = [];
                        const walk = (coords) => {
                            if (!Array.isArray(coords)) return;
                            if (
                                coords.length >= 2 &&
                                typeof coords[0] === 'number' &&
                                typeof coords[1] === 'number'
                            ) {
                                latLngArray.push({ lat: coords[0], lng: coords[1] });
                                return;
                            }
                            coords.forEach(walk);
                        };
                        walk(paths);
                        return latLngArray;
                    };
                    const varLinesWithEvents = varLines
                        .map((line) => {
                            if (!line) return null;
                            let lineId = parseInt(line.id, 10);
                            if (isNaN(lineId)) {
                                lineId = nextLineIdRef.current++;
                            }
                            const latLngArray = createVarLatLngArray(line.paths);
                            if (latLngArray.length < 2) return null;
                            return {
                                id: lineId,
                                latlngs: latLngArray,
                                properties: { ...(line.properties || {}), lineId, tasaVariable: true },
                            };
                        })
                        .filter(Boolean);

                    setLines(varLinesWithEvents);
                    setLineasNoFiltradas(varLinesWithEvents);
                    setBufferedLines([]);
                    setBufferedIntersections([]);
                    setKmlPolygons([]);
                    setPolygons(varPolygons);
                    setPolygonsProperties(varPolygonProperties);
                    setVarPoints(varPointFeatures);
                    setIsTasaVariableMap(true);
                    setTasaVariableBounds(Array.isArray(data.bounds) ? data.bounds : null);
                    setIsKml(true);
                    setIsFilteringLines(false);
                    setActionHistory([]);
                    setAreasCalculated(true);

                    if (typeof setOnAreasCalculatedLoader === 'function') {
                        setOnAreasCalculatedLoader(true);
                    }

                    fetchAndApplyTasaVariableSummary(data.summary || data.tasaVariableSummary || data.varSummary || null);

                    console.info('[VAR map] capas listas para pintar', {
                        lines: varLinesWithEvents.length,
                        polygons: varPolygons.length,
                        points: data.pointsMeta?.total || varPointFeatures.length,
                        renderedPoints: varPointFeatures.length,
                        bounds: data.bounds || null,
                    });
                    return;
                }

                setVarPoints([]);
                setTasaVariableRemoteLayers(null);
                setIsTasaVariableMap(false);
                setTasaVariableBounds(null);

                const createLatLngArray = (paths) => {
                    let latLngArray = [];
                    const processCoordinates = (coords) => {
                        if (Array.isArray(coords[0])) {
                            coords.forEach((coord) => { processCoordinates(coord); });
                        } else {
                            if (Array.isArray(coords) && coords.length === 2 &&
                                typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                                const [lat, lng] = coords;
                                latLngArray.push({ lat, lng });
                            }
                        }
                    };
                    if (paths) processCoordinates(paths);
                    return latLngArray;
                };
                if (data.filtradas && data.filtradas.lines) {
                    const filtradasLines = data.filtradas.lines;
                    const filtradasPolygons = data.filtradas.polygons || [];
                    const lineIds = [];
                    const filtradasLinesWithEvents = filtradasLines
                        .map((line) => {
                            if (!line) return null;
                            let lineId = parseInt(line.id, 10);
                            if (isNaN(lineId)) {
                                lineId = nextLineIdRef.current++;
                            }
                            lineIds.push(lineId);
                            const latLngArray = createLatLngArray(line.paths);
                            if (latLngArray.length < 2) return null;
                            const properties = { ...line.properties, lineId };
                            return { id: lineId, latlngs: latLngArray, properties };
                        })
                        .filter((line) => line !== null);

                    const maxLineId = Math.max(...lineIds);
                    if (!isNaN(maxLineId) && nextLineIdRef.current <= maxLineId) {
                        nextLineIdRef.current = maxLineId + 1;
                    }

                    setLines(filtradasLinesWithEvents);
                    setIsKml(true);
                    setKmlPolygons(filtradasPolygons);
                    setIsFilteringLines(true);
                    setActionHistory([]);
                }

                if (data.noFiltradas && data.noFiltradas.lines) {
                    const noFiltradasLines = data.noFiltradas.lines;
                    const lineIds = [];
                    const noFiltradasLinesWithEvents = noFiltradasLines
                        .map((line) => {
                            if (!line) return null;
                            let lineId = parseInt(line.id, 10);
                            if (isNaN(lineId)) {
                                lineId = nextLineIdRef.current++;
                            }
                            lineIds.push(lineId);
                            const latLngArray = createLatLngArray(line.paths);
                            if (latLngArray.length < 2) return null;
                            const properties = { lineId };
                            return { id: lineId, latlngs: latLngArray, properties };
                        })
                        .filter((line) => line !== null);

                    const maxLineId = Math.max(...lineIds);
                    if (!isNaN(maxLineId) && nextLineIdRef.current <= maxLineId) {
                        nextLineIdRef.current = maxLineId + 1;
                    }

                    setLineasNoFiltradas(noFiltradasLinesWithEvents);
                }

                if (data.lines && !data.noFiltradas) {
                    const { lines: dlines, polygons: dpolygons } = data;
                    const lineIds = [];
                    const linesWithEvents = dlines
                        .map((line) => {
                            if (!line) return null;
                            let lineId = parseInt(line.id, 10);
                            if (isNaN(lineId)) {
                                lineId = nextLineIdRef.current++;
                            }
                            lineIds.push(lineId);
                            const latLngArray = createLatLngArray(line.paths);
                            if (latLngArray.length < 2) return null;
                            const properties = { ...line.properties, lineId };
                            return { id: lineId, latlngs: latLngArray, properties };
                        })
                        .filter((line) => line !== null);

                    const maxLineId = Math.max(...lineIds);
                    if (!isNaN(maxLineId) && nextLineIdRef.current <= maxLineId) {
                        nextLineIdRef.current = maxLineId + 1;
                    }

                    setLines(linesWithEvents);
                    setIsKml(true);
                    const finalPolygons = [];
                    const polygonsPropertiesArray = [];
                    (dpolygons || []).forEach((dp) => {
                        if (!dp || !Array.isArray(dp.rings)) return;

                        dp.rings.forEach((maybeSubRing) => {
                            let realRing = maybeSubRing;
                            if (
                                Array.isArray(maybeSubRing) &&
                                maybeSubRing.length === 1 &&
                                Array.isArray(maybeSubRing[0])
                            ) {
                                realRing = maybeSubRing[0];
                            }

                            const normalized = normalizeRing(realRing);

                            if (normalized.length >= 4) {
                                finalPolygons.push(normalized);
                                polygonsPropertiesArray.push(dp.properties || {});
                            }
                        });
                    });

                    setPolygons(finalPolygons);
                    setPolygonsProperties(polygonsPropertiesArray);
                    setIsFilteringLines(false);
                    setActionHistory([]);

                }

                if (!data.lines && data.polygons) {
                    const dpolygons = data.polygons || [];
                    const formattedPolygons = dpolygons.map((poly, index) => {
                        if (!poly || !Array.isArray(poly.polygon) || !Array.isArray(poly.polygon[0])) return [];
                        const formatted = formatPolygon(poly.polygon[0]);
                        return formatted;
                    });

                    setPolygonsProperties(dpolygons.map((poly) => poly.properties || {}));
                    setPolygons(formattedPolygons);
                    setIsFilteringLines(false);
                }

            }
        };


        if (socket && socketSessionID) {
            socket.on(`${socketSessionID}:updateGeoJSONLayer`, (geojsonData) => {
                // La tasa variable se resuelve en segundo plano. Si el backend
                // avisa de un fallo, no se pasan capas vacías al worker: borraría
                // el mapa. El aviso al usuario ya llega por progressUpdate.
                if (geojsonData?.status === 'error') {
                    console.warn('[VAR] El backend reportó un error:', geojsonData?.error);
                    return;
                }

                const socketSummary = geojsonData?.tasaVariableSummary || geojsonData?.summary || null;
                const socketRadius = Number(socketSummary?.BUFFER_RADIO_M ?? socketSummary?.bufferRadioM);
                const expectedRadius = latestVarBufferRadiusRef.current;

                // Una petición anterior puede finalizar después que la más reciente.
                // Evitamos que un socket tardío vuelva a pintar capas calculadas con
                // un radio que el usuario ya cambió en el frontend.
                if (
                    geojsonData?.tasaVariable &&
                    Number.isFinite(expectedRadius) &&
                    Number.isFinite(socketRadius) &&
                    Math.abs(socketRadius - expectedRadius) > 1e-9
                ) {
                    console.info('[VAR buffer] Se ignoró una actualización obsoleta del socket.', {
                        socketRadius,
                        expectedRadius,
                    });
                    return;
                }

                if (workerRef.current) {
                    workerRef.current.postMessage({
                        action: 'processGeoJsonData',
                        geojsonData,
                        type: tipoAnalisis,
                        activarEdicionInteractiva,
                    });
                }
            });
        }

        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
            }
            if (socket && socketSessionID) {
                socket.off(`${socketSessionID}:updateGeoJSONLayer`);
            }
        };
    }, [activarEdicionInteractiva, socket, socketSessionID, tipoAnalisis, formatPolygon, fetchAndApplyTasaVariableSummary]);

    // Actualizar worker cuando cambia activarEdicionInteractiva
    useEffect(() => {
        if (workerRef.current) {
            workerRef.current.postMessage({ action: 'setActivarEdicionInteractiva', activarEdicionInteractiva });
        }
    }, [activarEdicionInteractiva]);

    const applyTasaVariableRecalculationResult = useCallback((result) => {
        if (!result || result.status === 'error') return;

        const urls = result.signed_urls || result.signedUrls || {};
        const geojsonData = {
            tasaVariable: true,
            tasaVariableSummary: result.summary || null,
            summary: result.summary || null,
            tasaVariableLayers: {
                geometriaAplicacion: urls.geometria_aplicacion || null,
                lineasAplicacion: urls.lineas_aplicacion,
                lineasAplicacionOverview: urls.lineas_aplicacion_overview,
                lineasAplicadasEnMaleza: urls.lineas_aplicadas_en_maleza,
                lineasAplicadasFueraMaleza: urls.lineas_aplicadas_fuera_maleza,
                malezaObjetivo: urls.maleza_objetivo,
                malezaObjetivoOverview: urls.maleza_objetivo_overview,
                malezaNoAplicada: urls.maleza_no_aplicada,
                malezaNoAplicadaOverview: urls.maleza_no_aplicada_overview,
                lineasRecorridoCompleto: urls.lineas_recorrido_completo,
            },
        };

        if (workerRef.current) {
            workerRef.current.postMessage({
                action: 'processGeoJsonData',
                geojsonData,
                type: tipoAnalisis,
                activarEdicionInteractiva,
            });
        }

        fetchAndApplyTasaVariableSummary(result.summary || null);
    }, [activarEdicionInteractiva, fetchAndApplyTasaVariableSummary, tipoAnalisis]);

    useEffect(() => {
        // Franjas (polígonos): la cobertura son las franjas reales; no existe
        // recálculo de buffer. Con puntos (líneas) sí, en APS y en Herbicidas.
        if (!isTasaVariableMap || !analisisId || isPolygonTasaVariable) {
            prevVarBufferHashRef.current = '';
            latestVarBufferRadiusRef.current = null;
            return;
        }

        const radius = Number(bufferValue);
        if (!isBufferActive || !Number.isFinite(radius) || radius <= 0) {
            prevVarBufferHashRef.current = '';
            latestVarBufferRadiusRef.current = null;
            if (varBufferTimeoutRef.current) {
                clearTimeout(varBufferTimeoutRef.current);
                varBufferTimeoutRef.current = null;
            }
            if (varBufferRequestAbortRef.current) {
                try {
                    varBufferRequestAbortRef.current.abort();
                } catch (e) { /* noop */ }
                varBufferRequestAbortRef.current = null;
            }
            return;
        }

        latestVarBufferRadiusRef.current = radius;
        const nextHash = `${analisisId}:${radius.toFixed(6)}`;
        if (prevVarBufferHashRef.current === nextHash) {
            return;
        }
        prevVarBufferHashRef.current = nextHash;

        if (varBufferTimeoutRef.current) {
            clearTimeout(varBufferTimeoutRef.current);
            varBufferTimeoutRef.current = null;
        }
        if (varBufferRequestAbortRef.current) {
            try {
                varBufferRequestAbortRef.current.abort();
            } catch (e) { /* noop */ }
        }

        const controller = new AbortController();
        varBufferRequestAbortRef.current = controller;
        if (typeof setOnAreasCalculatedLoader === 'function') {
            setOnAreasCalculatedLoader(false);
        }

        varBufferTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await axios.post(
                    `${API_BASE_PYTHON_SERVICE}mapping/tasa_variable/recalcular_buffer`,
                    {
                        id_analisis: analisisId,
                        buffer_radio_m: radius,
                        session_id: socketSessionID || analisisId,
                        tipo_analisis: isHerbicidasAnalysis ? 'HERBICIDAS' : 'APLICACIONES_AEREAS',
                    },
                    { signal: controller.signal }
                );

                if (response?.data?.status === 'error') {
                    throw new Error(response.data.message || 'No se pudo recalcular el buffer VRA.');
                }

                // El recálculo pesado se ejecuta en segundo plano. La respuesta
                // final llegará por Socket.IO con las capas y métricas nuevas.
                if (response?.data?.status === 'processing') {
                    console.info('[VAR buffer] Recálculo programado en segundo plano.', {
                        analisisId,
                        radius,
                    });
                    return;
                }

                if (
                    Number.isFinite(latestVarBufferRadiusRef.current) &&
                    Math.abs(latestVarBufferRadiusRef.current - radius) > 1e-9
                ) {
                    return;
                }

                applyTasaVariableRecalculationResult(response?.data);
                setAreasCalculated(true);
            } catch (error) {
                if (
                    (axios.isCancel && axios.isCancel(error)) ||
                    error?.name === 'CanceledError' ||
                    error?.name === 'AbortError'
                ) {
                    return;
                }
                prevVarBufferHashRef.current = '';
                console.error('[VAR buffer] Error al recalcular áreas y colores:', error);
            } finally {
                if (varBufferRequestAbortRef.current === controller) {
                    varBufferRequestAbortRef.current = null;
                }
                if (typeof setOnAreasCalculatedLoader === 'function') {
                    setOnAreasCalculatedLoader(true);
                }
            }
        }, 700);

        return () => {
            if (varBufferTimeoutRef.current) {
                clearTimeout(varBufferTimeoutRef.current);
                varBufferTimeoutRef.current = null;
            }
            if (varBufferRequestAbortRef.current === controller) {
                try {
                    controller.abort();
                } catch (e) { /* noop */ }
                varBufferRequestAbortRef.current = null;
            }
        };
    }, [
        isTasaVariableMap,
        analisisId,
        bufferValue,
        isBufferActive,
        socketSessionID,
        applyTasaVariableRecalculationResult,
        setOnAreasCalculatedLoader,
        isHerbicidasAnalysis,
        isPolygonTasaVariable,
    ]);

    const addBufferToLine = useCallback((line, widthMeters) => {
        try {
            if (!line || !Array.isArray(line.latlngs)) return null;

            // Ancho numérico del buffer (radio en metros)
            const w = Number(widthMeters);
            if (!Number.isFinite(w) || w <= 0) {
                // Si el buffer es 0 o inválido, no generamos polígono
                return null;
            }

            // 1) Coords válidas [lng, lat] sin duplicados consecutivos
            const raw = line.latlngs
                .map(pt => [pt.lng, pt.lat])
                .filter(c => Number.isFinite(c[0]) && Number.isFinite(c[1]));

            if (raw.length < 2) return null;

            const coords = [];
            for (let i = 0; i < raw.length; i++) {
                const prev = coords[coords.length - 1];
                const cur  = raw[i];
                if (
                    !prev ||
                    Math.abs(prev[0] - cur[0]) > 1e-9 ||
                    Math.abs(prev[1] - cur[1]) > 1e-9
                ) {
                    coords.push(cur);
                }
            }
            if (coords.length < 2) return null;

            // 2) Buffer suave alrededor de la línea
            const ls = turfLineString(coords);
            let buffered = turfBuffer(ls, w, {
                units: 'meters',
                steps: 16, // más grande => curvas más suaves
            });

            if (!buffered || !buffered.geometry) return null;

            // 3) Resolver auto-intersecciones simples
            if (buffered.geometry.type === 'Polygon') {
                try {
                    const unk = turfUnkinkPolygon(buffered);
                    if (unk && Array.isArray(unk.features) && unk.features.length > 0) {
                        if (unk.features.length === 1) {
                            buffered = unk.features[0];
                        } else {
                            buffered = {
                                type: 'Feature',
                                properties: {},
                                geometry: {
                                    type: 'MultiPolygon',
                                    coordinates: unk.features.map(f => f.geometry.coordinates),
                                },
                            };
                        }
                    }
                } catch (e) {
                    console.warn('unkinkPolygon failed in addBufferToLine:', e);
                }
            }

            // 4) Limpieza final de coordenadas
            buffered = turfCleanCoords(buffered);

            const t = buffered.geometry?.type;
            if (t !== 'Polygon' && t !== 'MultiPolygon') return null;

            buffered.properties = {
                ...(line.properties || {}),
                originalLineId: line.id,
            };

            return buffered;
        } catch (err) {
            console.error('Error in addBufferToLine:', err);
            return null;
        }
    }, []);

    useEffect(() => {
        const numericWidth = Number(bufferValue);

        // En VAR el buffer se recalcula en el servicio GIS para actualizar
        // métricas y capas clasificadas. No dibujamos ni enviamos buffers locales.
        if (isTasaVariableMap) {
            setBufferedLines([]);
            setBufferedIntersections([]);
            setAreasCalculated(true);
            return;
        }

        if (
            !isBufferActive ||
            !Array.isArray(lines) ||
            lines.length === 0 ||
            !Number.isFinite(numericWidth) ||
            numericWidth <= 0
        ) {
            // Sin buffer válido: limpiamos todo
            setBufferedLines([]);
            setBufferedIntersections([]);
            setAreasCalculated(false);
            return;
        }

        const newBufferedLines = lines
            .map((line) => addBufferToLine(line, numericWidth))
            .filter((bufferedLine) => bufferedLine !== null);

        setBufferedLines(newBufferedLines);
        setAreasCalculated(false);
    }, [bufferValue, isBufferActive, addBufferToLine, lines, isTasaVariableMap]);

    const normalizePolygon = (polygon) => {
        if (!Array.isArray(polygon)) return null;
        const normalized = polygon.map(coordPair => {
            if (Array.isArray(coordPair) && coordPair.length >= 2) {
                return [coordPair[1], coordPair[0]];
            }
            return null;
        }).filter(coord => coord !== null);

        if (
            normalized.length >= 3 &&
            (normalized[0][0] !== normalized[normalized.length - 1][0] ||
                normalized[0][1] !== normalized[normalized.length - 1][1])
        ) {
            normalized.push([...normalized[0]]);
        }

        if (normalized.length < 4) {
            return null;
        }
        return normalized;
    };

    // Redondea coordenadas a ~1cm de precisión. `turfUnion`/`turfDifference` usan
    // internamente una sweep line (polygon-clipping) que puede lanzar
    // "Unable to find segment ... in SweepLine tree" cuando hay vértices casi
    // duplicados o ruido de punto flotante (típico en polígonos GPS de vuelos).
    // Redondear + limpiar coordenadas colineales evita disparar ese bug.
    const roundCoordinatesDeep = useCallback((node, precision = 7) => {
        const factor = 10 ** precision;
        if (Array.isArray(node)) {
            if (node.length >= 2 && typeof node[0] === 'number' && typeof node[1] === 'number') {
                return [Math.round(node[0] * factor) / factor, Math.round(node[1] * factor) / factor];
            }
            return node.map((child) => roundCoordinatesDeep(child, precision));
        }
        return node;
    }, []);

    const sanitizeFeatureForBooleanOp = useCallback((feature) => {
        if (!feature || !feature.geometry) return feature;
        try {
            const cleaned = turfCleanCoords(feature);
            if (cleaned?.geometry?.coordinates) {
                cleaned.geometry.coordinates = roundCoordinatesDeep(cleaned.geometry.coordinates);
            }
            return cleaned;
        } catch (e) {
            return feature;
        }
    }, [roundCoordinatesDeep]);

    const calculateNonIntersectedAreas = useCallback((polygons) => {
        if (!Array.isArray(polygons) || polygons.length === 0) return [];
        if (polygons.length === 1) {
            return [polygons[0]];
        }

        try {
            let unionOfAllPolygons = sanitizeFeatureForBooleanOp(turfPolygon([polygons[0]]));

            for (let i = 1; i < polygons.length; i++) {
                try {
                    const nextFeature = sanitizeFeatureForBooleanOp(turfPolygon([polygons[i]]));
                    const merged = turfUnion(unionOfAllPolygons, nextFeature);
                    if (merged) {
                        unionOfAllPolygons = sanitizeFeatureForBooleanOp(merged);
                    }
                } catch (unionError) {
                    // Un polígono con topología degenerada no debe tumbar el cálculo completo:
                    // se omite solo ese polígono del union y se continúa con el resto.
                    console.warn('Área no intersectada: se omitió un polígono del union por error topológico:', unionError?.message || unionError);
                }
            }

            const nonIntersected = [];
            polygons.forEach((polygon) => {
                try {
                    const feature = sanitizeFeatureForBooleanOp(turfPolygon([polygon]));
                    const difference = turfDifference(feature, unionOfAllPolygons);
                    if (difference) {
                        nonIntersected.push(difference.geometry.coordinates);
                    }
                } catch (diffError) {
                    // Igual que arriba: se omite este polígono puntual en vez de vaciar
                    // el resultado completo por un único segmento problemático.
                    console.warn('Área no intersectada: se omitió un polígono en el cálculo de diferencia por error topológico:', diffError?.message || diffError);
                }
            });
            return nonIntersected;
        } catch (error) {
            console.error('Error calculando áreas no intersectadas:', error);
            return [];
        }
    }, [sanitizeFeatureForBooleanOp]);

    useEffect(() => {
        if (Array.isArray(polygons) && polygons.length > 0) {
            const newNonIntersectedAreas = calculateNonIntersectedAreas(polygons);
            setNonIntersectedAreas(newNonIntersectedAreas);
        }
    }, [polygons, calculateNonIntersectedAreas]);

    useEffect(() => {
        const hasSpeed = polygonsProperties.some((prop) => typeof prop?.VELOCIDAD === 'number');
        const hasAltitude = polygonsProperties.some((prop) => typeof prop?.ALTURA === 'number');
        const hasRealDose = polygonsProperties.some((prop) => typeof prop?.DOSISREAL === 'number');

        setAvailableFilters({
            speed: hasSpeed,
            altitude: hasAltitude,
            realDose: hasRealDose,
        });

    }, [polygonsProperties]);

    const handleLineClick = useCallback((e) => {
        const feature = e?.features?.[0];
        const lngLat = e?.lngLat;
        if (!feature || !lngLat) return;

        if (activeToolRef.current === 'delete') {
            const lineId = feature.properties?.lineId;
            if (typeof lineId !== 'number') return;
            const lineInState = lines.find((l) => l.id === lineId);
            if (lineInState) {
                setLines((prevLines) => prevLines.filter((l) => l.id !== lineInState.id));
                setActionHistory((prevHistory) => [...prevHistory, { type: 'delete', line: lineInState }]);
            }
            return;
        }

        const properties = feature.properties || {};
        const coordinates = feature.geometry.coordinates;
        const lineString = turfLineString(coordinates);
        const lengthKm = turfLength(lineString, { units: 'kilometers' });
        const lengthMiles = turfLength(lineString, { units: 'miles' });
        const lengthMeters = lengthKm * 1000;

        const propertyList =
            Object.keys(properties).length > 0
                ? Object.entries(properties).map(([key, value]) => (
                    <li key={key}><strong>{key}:</strong> {value !== undefined && value !== null ? String(value) : 'N/A'}</li>
                ))
                : null;

        const popupContent = (
            <div>
                {propertyList && (
                    <div>
                        <strong>Información de aplicación:</strong>
                        <ul>
                            {propertyList}
                        </ul>
                    </div>
                )}
                <div>
                    <strong>Tamaño línea aplicación:</strong>
                    <ul>
                        <li>{lengthKm.toFixed(2)} km</li>
                        <li>{lengthMiles.toFixed(2)} mi</li>
                        <li>{lengthMeters.toFixed(2)} m</li>
                    </ul>
                </div>
            </div>
        );

        setPopupInfo({ lngLat, content: popupContent });
    }, [lines]);

    // == Cálculo áreas con polígonos ==
// == Cálculo áreas con polígonos (sin buffer) ==
    useEffect(() => {
        // Tasa Variable ya trae sus métricas calculadas desde el servicio Python.
        // No debe entrar al cálculo legacy de áreas, porque esos GeoJSON son polígonos/puntos masivos
        // y pueden bloquear el navegador o disparar errores topológicos de Turf.
        if (isTasaVariableMap) {
            setAreasCalculated(true);
            if (typeof setOnAreasCalculatedLoader === 'function') {
                setOnAreasCalculatedLoader(true);
            }
            return;
        }

        // Validaciones básicas
        if (!Array.isArray(polygons) || polygons.length === 0) return;
        if (!Array.isArray(polygonsData) || polygonsData.length === 0) return;
        if (!analisisId) return;

        // Para el cálculo SIN buffer, solo importa si existen líneas (tipo), no su geometría.
        // Evitamos re-ejecutar por cambios de referencia en `lines`.
        const hasAnyLines = Array.isArray(lines) && lines.length > 0;

        const tipo = isHerbicidasAnalysis
            ? (hasAnyLines ? 'herbicidas_helicoptero' : 'herbicidas_drone')
            : (hasAnyLines ? 'helicoptero' : 'drone');
        const bw = Number(bufferValue);
        const usingBuffer =
            isBufferActive &&
            Number.isFinite(bw) &&
            bw > 0 &&
            hasAnyLines;

        // Si el buffer está activo, el cálculo lo maneja el useEffect de buffer (más abajo)
        if (usingBuffer) return;

        // Anti-bucle: hash de la entrada (solo lo relevante para este cálculo SIN BUFFER)
        const polygonsHash = JSON.stringify(polygons);
        const polygonsDataHash = JSON.stringify(
            polygonsData.map((pd) => ({
                t: pd?.type ?? null,
                g: pd?.geometry ?? null,
            }))
        );

        const currentHash = JSON.stringify({
            polygonsHash,
            polygonsDataHash,
            analisisId,
            tipo,
        });

        if (prevPolygonsHashRef.current === currentHash) {
            // Nada cambió realmente => no recalculamos
            return;
        }
        prevPolygonsHashRef.current = currentHash;

        const calculateAreas = async () => {
            setOnAreasCalculatedLoader(false);

            const requestData = {
                bufferedLines: [],
                polygons: polygons.map((polygon) => ({
                    geometry: {
                        type: 'Polygon',
                        coordinates: [
                            // [lat, lng] => [lng, lat]
                            polygon.map(([lat, lon]) => [lon, lat]),
                        ],
                    },
                })),
                polygonsData: polygonsData.map((polygonData) => ({
                    type: polygonData.type,
                    geometry: polygonData.geometry,
                    properties: polygonData.properties,
                })),
                id_analisis: analisisId,
                tipo_analisis: tipo,
                session_id: socketSessionID,
            };

            try {
                const response = await axios.post(
                    `${API_BASE_PYTHON_SERVICE}mapping/calculate_areas`,
                    requestData
                );

                if (response.status === 200 && response.data && response.data.data) {
                    // Créditos por producto: el análisis Normal terminó bien.
                    confirmarConsumoApsSiPendiente(analisisId);
                    const {
                        areaSobreAplicada,
                        areaAplicada,
                        areaNoAplicada,
                        porcentajeDeVariacion,
                        porcentajeVariacionDosis,
                        areaAplicadaEnCalles,
                        areaAplicadaFueraDelLote,
                        dosisTotalDiferencia,
                        dosisTotalRealAplicada,
                        areaTotalLote,
                    } = response.data.data;

                    if (typeof onAreasCalculatedRef.current === 'function') {
                        onAreasCalculatedRef.current({
                            areaSobreAplicada,
                            areaAplicada,
                            areaNoAplicada,
                            porcentajeDeVariacion,
                            porcentajeVariacionDosis,
                            areaAplicadaEnCalles,
                            areaAplicadaFueraDelLote,
                            dosisTotalRealAplicada,
                            dosisTotalDiferencia,
                            areaTotalLote,
                        });
                    }
                }
            } catch (error) {
                console.error('Error al calcular áreas (sin buffer):', error);
            } finally {
                setOnAreasCalculatedLoader(true);
            }
        };

        // Debounce para no bombardear el backend cuando se mueve algo rápido
        if (noBufferTimeoutRef.current) clearTimeout(noBufferTimeoutRef.current);
        noBufferTimeoutRef.current = setTimeout(calculateAreas, 800);

        return () => {
            if (noBufferTimeoutRef.current) {
                clearTimeout(noBufferTimeoutRef.current);
            }
        };
    }, [
        polygons,
        polygonsData,
        analisisId,
        setOnAreasCalculatedLoader,
        socketSessionID,
        // Solo nos importa si hay o no líneas (tipo), no el objeto completo.
        lines?.length,
        isBufferActive,
        bufferValue,
        isTasaVariableMap,
    ]);


// == Cálculo áreas con buffer (líneas bufferizadas) ==
//
// IMPORTANTÍSIMO:
// Antes este effect devolvía un cleanup que cancelaba el timeout/abortaba la petición
// en CADA re-render por cambios de dependencias. Si React re-ejecutaba el effect
// (p.ej. polygonsData/bufferedLines cambia de referencia) antes de que corriera el timeout,
// el cleanup cancelaba la petición y el nuevo effect podía "return" por hash igual,
// dejando el cálculo sin dispararse hasta que el usuario cambiara el valor otra vez.
//
// Solución: NO cancelamos en cleanup de re-ejecución; solo cancelamos cuando:
//  - el buffer se desactiva / no hay bufferedLines
//  - o cuando realmente cambia el hash y vamos a programar una nueva petición.
    useEffect(() => {
        // VAR usa un endpoint específico que recalcula cobertura, métricas y colores
        // directamente desde las líneas fuente persistidas en GCS.
        if (isTasaVariableMap) {
            setBufferedIntersections([]);
            setAreasCalculated(true);
            prevBufferCalcHashRef.current = '';
            return;
        }

        // 1) Si no hay buffers, limpiamos todo y cancelamos cualquier petición pendiente
        if (!Array.isArray(bufferedLines) || bufferedLines.length === 0) {
            setBufferedIntersections([]);
            setAreasCalculated(false);
            prevBufferCalcHashRef.current = '';

            // Cancelar petición anterior (si existía)
            if (bufferRequestAbortRef.current) {
                try {
                    bufferRequestAbortRef.current.abort();
                } catch (e) { /* noop */ }
                bufferRequestAbortRef.current = null;
            }

            // Limpiar timeout
            if (bufferTimeoutRef.current) {
                try {
                    clearTimeout(bufferTimeoutRef.current);
                } catch (e) { /* noop */ }
                bufferTimeoutRef.current = null;
            }
            return;
        }

        // 2) Intersecciones de buffers (solo cuando cambia bufferedLines)
        const calculateBufferedIntersections = (lines) => {
            const intersections = [];
            const seen = new Set();

            const polys = [];
            lines.forEach((feat, idx) => {
                if (!feat || !feat.geometry) return;
                const geom = feat.geometry;

                if (geom.type === 'Polygon') {
                    const poly = turfCleanCoords(turfPolygon(geom.coordinates));
                    polys.push({
                        feature: poly,
                        bufferIndex: idx,
                        originalLineId: feat.properties?.originalLineId ?? feat.originalLineId ?? idx,
                    });
                } else if (geom.type === 'MultiPolygon') {
                    geom.coordinates.forEach((coords, subIdx) => {
                        const poly = turfCleanCoords(turfPolygon(coords));
                        polys.push({
                            feature: poly,
                            bufferIndex: idx,
                            subIndex: subIdx,
                            originalLineId: feat.properties?.originalLineId ?? feat.originalLineId ?? idx,
                        });
                    });
                }
            });

            for (let i = 0; i < polys.length; i++) {
                const A = polys[i].feature;
                for (let j = i + 1; j < polys.length; j++) {
                    const B = polys[j].feature;
                    try {
                        const inter = turfIntersect(A, B);
                        if (!inter || !inter.geometry || !inter.geometry.coordinates) continue;

                        const { type, coordinates } = inter.geometry;

                        const pushRing = (ring) => {
                            if (!Array.isArray(ring) || ring.length < 4) return;

                            try {
                                const poly = turfPolygon([ring]);
                                const area = turfArea(poly);
                                if (!Number.isFinite(area) || area <= 0) return;
                            } catch {
                                return;
                            }

                            const key = JSON.stringify(ring);
                            if (!seen.has(key)) {
                                seen.add(key);
                                intersections.push(ring);
                            }
                        };

                        if (type === 'Polygon') {
                            pushRing(coordinates[0]);
                        } else if (type === 'MultiPolygon') {
                            coordinates.forEach((polyCoords) => {
                                pushRing(polyCoords[0]);
                            });
                        }
                    } catch (err) {
                        console.error('Error calculando intersección de buffers:', err);
                    }
                }
            }

            setBufferedIntersections(intersections);
        };

        calculateBufferedIntersections(bufferedLines);

        // 3) Si no hay polígonos o no hay análisis, no llamamos backend
        if (!Array.isArray(polygonsData) || polygonsData.length === 0 || !analisisId) {
            return;
        }

        const bw = Number(bufferValue);
        const hasAnyLines = Array.isArray(lines) && lines.length > 0;
        const tipo = isHerbicidasAnalysis
            ? (hasAnyLines ? 'herbicidas_helicoptero' : 'herbicidas_drone')
            : (hasAnyLines ? 'helicoptero' : 'drone');


        // Usamos el buffer recortado (clipped) para el cálculo de áreas cuando CommonMap lo reporta.
        // Si aún no lo hemos recibido (primer render), usamos el buffer completo como fallback.
        const buffersForCalc =
            (clippedBufferReadyRef.current &&
                clippedBufferedLinesFC?.type === 'FeatureCollection' &&
                Array.isArray(clippedBufferedLinesFC.features))
                ? clippedBufferedLinesFC.features
                : bufferedLines;

        // 4) Hash de entrada (si no cambia, no reprogramamos)
        const bufferHash = JSON.stringify({
            analisisId,
            tipo,
            bw,
            polygonsData: polygonsData.map((pd) => ({ t: pd?.type ?? null, g: pd?.geometry ?? null })),
            bufferedLines: buffersForCalc.map((b) => b?.geometry ?? null),
        });

        if (prevBufferCalcHashRef.current === bufferHash) {
            return; // mismo input => dejamos correr el timeout ya programado (si existe)
        }
        prevBufferCalcHashRef.current = bufferHash;

        // 5) Vamos a programar una nueva petición => cancelamos la anterior (si existía)
        if (bufferTimeoutRef.current) {
            try {
                clearTimeout(bufferTimeoutRef.current);
            } catch (e) { /* noop */ }
            bufferTimeoutRef.current = null;
        }

        if (bufferRequestAbortRef.current) {
            try {
                bufferRequestAbortRef.current.abort();
            } catch (e) { /* noop */ }
            bufferRequestAbortRef.current = null;
        }

        const controller = new AbortController();
        bufferRequestAbortRef.current = controller;

        const requestId = ++bufferRequestIdRef.current;

        setOnAreasCalculatedLoader(false);

        // 6) Debounce para cambios rápidos en el ancho del buffer
        bufferTimeoutRef.current = setTimeout(async () => {
            try {
                const requestData = {
                    bufferedLines: buffersForCalc.filter((f) => f?.geometry).map((f) => ({ geometry: f.geometry })),
                    polygons: [],
                    polygonsData: polygonsData.map((polygonData) => ({
                        type: polygonData.type,
                        geometry: polygonData.geometry,
                        properties: polygonData.properties,
                    })),
                    id_analisis: analisisId,
                    tipo_analisis: tipo,
                    session_id: socketSessionID,
                };

                const response = await axios.post(
                    `${API_BASE_PYTHON_SERVICE}mapping/calculate_areas`,
                    requestData,
                    { signal: controller.signal }
                );

                // Ignorar respuesta vieja
                if (requestId !== bufferRequestIdRef.current) return;

                if (response.status === 200 && response.data && response.data.data) {
                    // Créditos por producto: el análisis Normal terminó bien.
                    confirmarConsumoApsSiPendiente(analisisId);
                    const {
                        areaSobreAplicada,
                        areaAplicada,
                        areaNoAplicada,
                        porcentajeDeVariacion,
                        porcentajeVariacionDosis,
                        areaAplicadaEnCalles,
                        areaAplicadaFueraDelLote,
                        dosisTotalDiferencia,
                        areaTotalLote,
                        dosisTotalRealAplicada,
                    } = response.data.data;

                    if (typeof onAreasCalculatedRef.current === 'function') {
                        onAreasCalculatedRef.current({
                            areaSobreAplicada,
                            areaAplicada,
                            areaNoAplicada,
                            porcentajeDeVariacion,
                            porcentajeVariacionDosis,
                            areaAplicadaEnCalles,
                            areaAplicadaFueraDelLote,
                            dosisTotalRealAplicada,
                            areaTotalLote,
                            dosisTotalDiferencia,
                        });
                    }
                    setAreasCalculated(true);
                } else {
                    console.error('Error al calcular áreas (buffer): Respuesta no válida del servidor');
                }
            } catch (error) {
                if (
                    (axios.isCancel && axios.isCancel(error)) ||
                    error?.name === 'CanceledError' ||
                    error?.name === 'AbortError'
                ) {
                    // cancelado
                } else {
                    console.error('Error al calcular áreas (buffer):', error);
                }
            } finally {
                if (requestId === bufferRequestIdRef.current) {
                    setOnAreasCalculatedLoader(true);
                    bufferRequestAbortRef.current = null;
                }
            }
        }, 700);
    }, [
        bufferedLines,
        clippedBufferedLinesFC,
        polygonsData,
        analisisId,
        setOnAreasCalculatedLoader,
        socketSessionID,
        bufferValue,
        isTasaVariableMap,
        // solo longitud (tipo)
        lines?.length,
    ]);


    useEffect(() => {
        if (!isTasaVariableMap || !Array.isArray(tasaVariableBounds) || tasaVariableBounds.length !== 2) {
            return;
        }

        const map = mapRef?.current;
        if (!map || typeof map.fitBounds !== 'function') {
            return;
        }

        const [[minLng, minLat], [maxLng, maxLat]] = tasaVariableBounds;
        const validBounds = [minLng, minLat, maxLng, maxLat].every((value) => Number.isFinite(value));
        if (!validBounds) {
            return;
        }

        const timeout = setTimeout(() => {
            try {
                map.fitBounds(
                    [[minLng, minLat], [maxLng, maxLat]],
                    { padding: 60, duration: 900, maxZoom: 17 }
                );
            } catch (error) {
                console.error('[VAR map] Error al centrar mapa:', error);
            }
        }, 250);

        return () => clearTimeout(timeout);
    }, [isTasaVariableMap, tasaVariableBounds, mapRef]);




    const handleUndo = useCallback(() => {
        if (actionHistory.length === 0) return;

        const lastAction = actionHistory[actionHistory.length - 1];
        let newLines = [...lines];

        switch (lastAction.type) {
            case 'cut':
                newLines = lastAction.originalLines || [];
                break;

            case 'draw':
                if (lastAction.line) {
                    newLines = newLines.filter((line) => line.id !== lastAction.line.id);
                }
                break;

            case 'delete':
                if (lastAction.line) {
                    newLines = [...newLines, lastAction.line];
                }
                break;

            case 'stretch':
                if (lastAction.lineId != null && Array.isArray(lastAction.originalLatlngs)) {
                    newLines = newLines.map((line) => {
                        if (!line || line.id !== lastAction.lineId) return line;
                        return {
                            ...line,
                            latlngs: lastAction.originalLatlngs.map((pt) => ({ ...pt })),
                        };
                    });
                }
                break;

            default:
                break;
        }

        setLines(newLines);
        setActionHistory((prev) => prev.slice(0, -1));
    }, [actionHistory, lines]);


    useEffect(() => {
        if (typeof registerUndoHandler === 'function') {
            registerUndoHandler(handleUndo);

            return () => {
                registerUndoHandler(null);
            };
        }
    }, [registerUndoHandler, handleUndo]);


    const deactivateTool = useCallback(() => {
        setActiveTool(null);
        if (currentToolCleanupRef.current) {
            currentToolCleanupRef.current();
            currentToolCleanupRef.current = null;
        }
        safeMapCall((map) => {
            try {
                if (map.getLayer('drawing-line-layer')) map.removeLayer('drawing-line-layer');
                if (map.getSource('drawing-line')) map.removeSource('drawing-line');
                if (map.getLayer('cutting-line-layer')) map.removeLayer('cutting-line-layer');
                if (map.getSource('cutting-line')) map.removeSource('cutting-line');
                // medición
                removeMeasurementLayers();
            } catch (error) {
                console.error('Error al desactivar herramienta:', error);
            }
        });
        // reset refs
        isDrawingRef.current = false;
        newLineCoordsRef.current = [];
        isCuttingRef.current = false;
        cutLineCoordsRef.current = [];
        isMeasuringRef.current = false;
        measureCoordsRef.current = [];
    }, [safeMapCall, setActiveTool, removeMeasurementLayers]);

    const autoPanMapFunction = useCallback((e) => {
        const map = mapRef?.current;
        if (!map || typeof map.getCanvas !== 'function') return;
        const canvas = map.getCanvas();
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const edgeThreshold = 50;

        const mouseX = e.point.x;
        const mouseY = e.point.y;

        const panOffset = { x: 0, y: 0 };

        if (mouseX < edgeThreshold) panOffset.x = -50;
        else if (mouseX > rect.width - edgeThreshold) panOffset.x = 50;

        if (mouseY < edgeThreshold) panOffset.y = -50;
        else if (mouseY > rect.height - edgeThreshold) panOffset.y = 50;

        if (panOffset.x !== 0 || panOffset.y !== 0) {
            safeMapCall((m) => { m.panBy([panOffset.x, panOffset.y], { duration: 100 }); });
        }
    }, [mapRef, safeMapCall]);


    const setupStretchMode = useCallback(() => {
        const map = mapRef?.current;
        if (!map || typeof map.on !== 'function') {
            return () => {};
        }

        // --- 1) Candidatos de snapping a partir de lineasNoFiltradas ---
        const unfilteredLines = unfilteredLinesRef.current || [];

        const snapCandidates = unfilteredLines
            .map((line) => {
                if (!line || !Array.isArray(line.latlngs) || line.latlngs.length < 2) return null;

                const coords = line.latlngs
                    .map((pt) => [pt.lng, pt.lat])
                    .filter(
                        (c) =>
                            Array.isArray(c) &&
                            Number.isFinite(c[0]) &&
                            Number.isFinite(c[1])
                    );

                if (coords.length < 2) return null;

                return {
                    id: line.id,
                    coords,
                    feature: turfLineString(coords),
                };
            })
            .filter(Boolean);

        // --- 2) Constantes de comportamiento ---
        const SNAP_MAX_METERS = 10;        // Máx. distancia para hacer snap al segmento no filtrado
        const DIR_MAX_DIFF_DEG = 25;       // Máx. diferencia angular con la dirección original
        const MAX_VERTEX_PIXEL_DIST = 25;  // Radio de "agarre" en píxeles para el vértice

        // Estado interno de arrastre
        const dragState = {
            isDragging: false,
            lineId:       null,
            vertexIndex:  null,
            lastLngLat:   null,
            baseBearing:  null,     // orientación del segmento original que estamos estirando
            baseSegDySign: 0,       // signo del movimiento vertical del segmento original
            snapLineId:   null,     // id de la línea no filtrada a la que nos "pegamos"
            originalLineLatlngs: null, // ⬅ NUEVO: copia de la línea antes de estirar
        };

        // Diferencia angular en [0, 180]
        const angleDiff = (a, b) => {
            if (!Number.isFinite(a) || !Number.isFinite(b)) return 180;
            let d = Math.abs(a - b);
            if (d > 180) d = 360 - d;
            return d;
        };

        // --- 3) Lógica de SNAP a lineasNoFiltradas ---
        const getSnapPointIfNeeded = (currentLngLat, lastLngLat) => {
            if (!snapCandidates.length || !lastLngLat) return currentLngLat;

            try {
                const currentPoint = turfPoint([currentLngLat.lng, currentLngLat.lat]);
                const dragBearing = turfBearing(
                    [lastLngLat.lng, lastLngLat.lat],
                    [currentLngLat.lng, currentLngLat.lat]
                );
                const dragDy = currentLngLat.lat - lastLngLat.lat;

                let best = null;

                for (const cand of snapCandidates) {
                    // Si ya nos "pegamos" a una línea, no saltamos a otra
                    if (dragState.snapLineId !== null && cand.id !== dragState.snapLineId) {
                        continue;
                    }

                    const snapped = turfNearestPointOnLine(cand.feature, currentPoint, { units: 'meters' });
                    if (!snapped || !snapped.geometry || !snapped.geometry.coordinates) continue;

                    const snappedCoord = snapped.geometry.coordinates;
                    const distMeters =
                        typeof snapped.properties?.dist === 'number'
                            ? snapped.properties.dist
                            : turfDistance(currentPoint, snapped, { units: 'meters' });

                    if (!Number.isFinite(distMeters) || distMeters > SNAP_MAX_METERS) continue;

                    const idx = snapped.properties?.index;
                    if (!Number.isFinite(idx) || idx < 0 || idx >= cand.coords.length - 1) continue;

                    const segStart = cand.coords[idx];
                    const segEnd   = cand.coords[idx + 1];

                    const segBearing = turfBearing(segStart, segEnd);
                    const segDy      = segEnd[1] - segStart[1];

                    // Segmento prácticamente horizontal / degenerado => no nos interesa
                    if (Math.abs(segDy) < 1e-10) continue;

                    // 1) Orientación compatible con el segmento ORIGINAL que estamos estirando
                    if (typeof dragState.baseBearing === 'number') {
                        const diffToBase = angleDiff(segBearing, dragState.baseBearing);
                        if (diffToBase > DIR_MAX_DIFF_DEG) continue;
                    }

                    // 2) Mismo "sentido vertical" que el segmento original (evitar líneas a la par que van al revés)
                    if (dragState.baseSegDySign !== 0 && Math.sign(segDy) !== dragState.baseSegDySign) {
                        continue;
                    }

                    // 3) Compatible con la dirección en la que estás arrastrando
                    const diffMove = angleDiff(segBearing, dragBearing);
                    if (diffMove > 60) continue;

                    // 4) Movimiento vertical coherente con el segmento candidato
                    if (dragDy * segDy <= 0) continue;

                    if (!best || distMeters < best.distMeters) {
                        best = {
                            distMeters,
                            lng: snappedCoord[0],
                            lat: snappedCoord[1],
                            candId: cand.id,
                        };
                    }
                }

                if (!best) return currentLngLat;

                // Fijamos la línea de snap para que no vaya saltando de una a otra
                if (dragState.snapLineId === null) {
                    dragState.snapLineId = best.candId;
                }

                // Nos vamos EXACTO al segmento no filtrado (si quieres "un poquito encima",
                // aquí podrías aplicar un pequeño offset en lat/lng).
                return { lng: best.lng, lat: best.lat };
            } catch (err) {
                console.error('Error en getSnapPointIfNeeded (stretch):', err);
                return currentLngLat;
            }
        };

        // --- 4) Localización del vértice más cercano en PÍXELES ---
        const mapboxMap =
            map.getMap && typeof map.getMap === 'function' ? map.getMap() : map;

        const findNearestVertex = (e) => {
            if (!mapboxMap || typeof mapboxMap.project !== 'function') {
                return { lineIndex: -1, vertexIndex: -1 };
            }

            const clickPoint = e.point;
            let bestLineIndex   = -1;
            let bestVertexIndex = -1;
            let bestDist2       = Infinity;

            const currentLines = linesRef.current || [];

            for (let li = 0; li < currentLines.length; li++) {
                const line = currentLines[li];
                if (!line || !Array.isArray(line.latlngs)) continue;

                for (let vi = 0; vi < line.latlngs.length; vi++) {
                    const pt = line.latlngs[vi];
                    if (!pt || !Number.isFinite(pt.lng) || !Number.isFinite(pt.lat)) continue;

                    const projected = mapboxMap.project([pt.lng, pt.lat]);
                    const dx = projected.x - clickPoint.x;
                    const dy = projected.y - clickPoint.y;
                    const d2 = dx * dx + dy * dy;

                    if (d2 < bestDist2) {
                        bestDist2       = d2;
                        bestLineIndex   = li;
                        bestVertexIndex = vi;
                    }
                }
            }

            if (bestLineIndex === -1) return { lineIndex: -1, vertexIndex: -1 };
            if (Math.sqrt(bestDist2) > MAX_VERTEX_PIXEL_DIST) {
                // Demasiado lejos de cualquier vértice
                return { lineIndex: -1, vertexIndex: -1 };
            }
            return { lineIndex: bestLineIndex, vertexIndex: bestVertexIndex };
        };

        // --- 5) Actualizar cursor (manita) cuando estás cerca de un vértice ---
        const updateCursorHover = (e) => {
            if (dragState.isDragging) return;
            const canvas = map.getCanvas && map.getCanvas();
            if (!canvas) return;

            const { lineIndex, vertexIndex } = findNearestVertex(e);
            if (lineIndex !== -1 && vertexIndex !== -1) {
                // Manita lista para agarrar
                canvas.style.cursor = 'grab';
            } else {
                canvas.style.cursor = '';
            }
        };

        // --- 6) Inicio de arrastre ---
        const startDrag = (e) => {
            // Solo botón izquierdo
            if (e.originalEvent && e.originalEvent.button !== 0) return;

            const { lineIndex, vertexIndex } = findNearestVertex(e);
            if (lineIndex === -1 || vertexIndex === -1) return;

            const currentLines = linesRef.current || [];
            const line = currentLines[lineIndex];
            if (!line || !Array.isArray(line.latlngs)) return;

            dragState.isDragging  = true;
            dragState.lineId      = line.id;
            dragState.vertexIndex = vertexIndex;
            dragState.lastLngLat  = { lng: e.lngLat.lng, lat: e.lngLat.lat };
            dragState.snapLineId  = null;
            if (Array.isArray(line.latlngs)) {
                dragState.originalLineLatlngs = line.latlngs.map((pt) => ({ ...pt }));
            } else {
                dragState.originalLineLatlngs = null;
            }
            const pts = line.latlngs;
            if (pts.length >= 2) {
                let a = null;
                let b = null;
                if (vertexIndex > 0 && vertexIndex < pts.length) {
                    a = pts[vertexIndex - 1];
                    b = pts[vertexIndex];
                } else if (vertexIndex === 0 && pts.length > 1) {
                    a = pts[0];
                    b = pts[1];
                } else if (vertexIndex === pts.length - 1 && pts.length > 1) {
                    a = pts[pts.length - 2];
                    b = pts[pts.length - 1];
                }

                if (a && b) {
                    dragState.baseBearing = turfBearing(
                        [a.lng, a.lat],
                        [b.lng, b.lat]
                    );
                    const dyBase = b.lat - a.lat;
                    dragState.baseSegDySign = dyBase > 0 ? 1 : dyBase < 0 ? -1 : 0;
                } else {
                    dragState.baseBearing    = null;
                    dragState.baseSegDySign  = 0;
                }
            } else {
                dragState.baseBearing   = null;
                dragState.baseSegDySign = 0;
            }

            // Deshabilitamos el pan del mapa mientras arrastras
            if (map.dragPan && typeof map.dragPan.disable === 'function') {
                map.dragPan.disable();
            }
            const canvas = map.getCanvas && map.getCanvas();
            if (canvas) {
                canvas.style.cursor = 'grabbing';
            }
        };

        // --- 7) Movimiento de arrastre ---
        const moveDrag = (e) => {
            // Si no estamos arrastrando, solo actualizamos cursor/manita
            if (!dragState.isDragging) {
                updateCursorHover(e);
                return;
            }

            try {
                // Auto-pan cerca de los bordes
                autoPanMapFunction(e);
            } catch (err) {
                // noop
            }

            const rawLngLat    = { lng: e.lngLat.lng, lat: e.lngLat.lat };
            const snappedLngLat = getSnapPointIfNeeded(
                rawLngLat,
                dragState.lastLngLat || rawLngLat
            );
            dragState.lastLngLat = rawLngLat;

            // Actualizamos SOLO el vértice que estás moviendo
            setLines((prevLines) =>
                prevLines.map((line) => {
                    if (!line || line.id !== dragState.lineId || !Array.isArray(line.latlngs)) {
                        return line;
                    }
                    const newLatLngs = [...line.latlngs];
                    const idx = Math.max(
                        0,
                        Math.min(dragState.vertexIndex, newLatLngs.length - 1)
                    );
                    newLatLngs[idx] = {
                        lat: snappedLngLat.lat,
                        lng: snappedLngLat.lng,
                    };
                    return { ...line, latlngs: newLatLngs };
                })
            );
        };

        const endDrag = () => {
            if (!dragState.isDragging) return;

            const movedLineId          = dragState.lineId;
            const originalLineLatlngs  = dragState.originalLineLatlngs;

            dragState.isDragging    = false;
            dragState.lineId        = null;
            dragState.vertexIndex   = null;
            dragState.lastLngLat    = null;
            dragState.baseBearing   = null;
            dragState.baseSegDySign = 0;
            dragState.snapLineId    = null;
            dragState.originalLineLatlngs = null;

            if (map.dragPan && typeof map.dragPan.enable === 'function') {
                map.dragPan.enable();
            }
            const canvas = map.getCanvas && map.getCanvas();
            if (canvas) {
                canvas.style.cursor = '';
            }

            if (movedLineId != null && Array.isArray(originalLineLatlngs)) {
                const currentLines = linesRef.current || [];
                const movedLine = currentLines.find((l) => l && l.id === movedLineId);

                if (movedLine && Array.isArray(movedLine.latlngs)) {
                    const changed =
                        movedLine.latlngs.length !== originalLineLatlngs.length ||
                        movedLine.latlngs.some((pt, idx) =>
                            pt.lat !== originalLineLatlngs[idx].lat ||
                            pt.lng !== originalLineLatlngs[idx].lng
                        );

                    if (changed) {
                        setActionHistory((prev) => [
                            ...prev,
                            {
                                type: 'stretch',
                                lineId: movedLineId,
                                originalLatlngs: originalLineLatlngs,
                            },
                        ]);
                    }
                }
            }
        };


        // --- 9) Suscripción de eventos al mapa ---
        map.on('mousedown', startDrag);
        map.on('mousemove', moveDrag);
        map.on('mouseup',   endDrag);
        map.on('mouseleave', endDrag);

        // --- 10) Función de limpieza ---
        return () => {
            try {
                map.off('mousedown', startDrag);
                map.off('mousemove', moveDrag);
                map.off('mouseup',   endDrag);
                map.off('mouseleave', endDrag);
            } catch (error) {
                console.error('Error limpiando stretch tool:', error);
            }
            endDrag();
        };
    }, [mapRef, setLines, autoPanMapFunction, setActionHistory]);




    const resetDrawing = useCallback(() => {
        if (mapRef.current) {
            isDrawingRef.current = false;
            newLineCoordsRef.current = [];
            removeDrawingLayer();
        }
    }, [mapRef, removeDrawingLayer]);

    const resetCutting = useCallback(() => {
        if (mapRef.current) {
            isCuttingRef.current = false;
            cutLineCoordsRef.current = [];
            removeCuttingLayer();
        }
    }, [mapRef, removeCuttingLayer]);

    useEffect(() => {

        if (currentToolCleanupRef.current) {
            currentToolCleanupRef.current();
            currentToolCleanupRef.current = null;
        }

        isDrawingRef.current = false;
        newLineCoordsRef.current = [];
        isCuttingRef.current = false;
        cutLineCoordsRef.current = [];
        isMeasuringRef.current = false;
        measureCoordsRef.current = [];
        removeMeasurementLayers();

        const map = mapRef?.current;
        if (!map || typeof map.isStyleLoaded !== 'function' || !map.isStyleLoaded()) {
            return;
        }

        if (activeTool === 'draw') {
            const onMapClick = (e) => {
                const coords = [e.lngLat.lng, e.lngLat.lat];
                if (!isDrawingRef.current) {
                    isDrawingRef.current = true;
                    newLineCoordsRef.current = [coords];
                } else {
                    newLineCoordsRef.current.push(coords);
                }
                updateDrawingLayer(newLineCoordsRef.current);
            };

            const onMouseMove = (e) => {
                if (isDrawingRef.current && newLineCoordsRef.current.length > 0) {
                    const coords = [e.lngLat.lng, e.lngLat.lat];
                    const currentLine = [...newLineCoordsRef.current, coords];
                    updateDrawingLayer(currentLine);
                    autoPanMapFunction(e);
                    autoPanMap(e);
                }
            };

            const onRightClick = (e) => {
                e.preventDefault();

                if (!isDrawingRef.current || newLineCoordsRef.current.length < 2) {
                    return;
                }
                const lineObj = {
                    id: nextLineIdRef.current++,
                    latlngs: newLineCoordsRef.current.map((coord) => ({ lat: coord[1], lng: coord[0] })),
                    properties: { lineId: nextLineIdRef.current - 1 },
                };
                setLines((prevLines) => [...prevLines, lineObj]);
                setActionHistory((prevHistory) => [...prevHistory, { type: 'draw', line: lineObj }]);

                isDrawingRef.current = false;
                newLineCoordsRef.current = [];
                resetDrawing();
                updateDrawingLayer([]);
            };

            const updateDrawingLayer = (coordinates) => {
                safeMapCall((m) => {
                    const source = m.getSource('drawing-line');
                    if (source && source.setData) {
                        source.setData({
                            type: 'Feature',
                            geometry: { type: 'LineString', coordinates: coordinates },
                        });
                    } else {
                        if (!m.getSource('drawing-line')) {
                            m.addSource('drawing-line', {
                                type: 'geojson',
                                data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coordinates } },
                            });
                        }
                        if (!m.getLayer('drawing-line-layer')) {
                            m.addLayer({
                                id: 'drawing-line-layer',
                                type: 'line',
                                source: 'drawing-line',
                                paint: { 'line-color': '#ff0000', 'line-width': 2 },
                            });
                        }
                    }
                });
            };

            map.on('click', onMapClick);
            map.on('contextmenu', onRightClick);
            map.on('mousemove', onMouseMove);

            currentToolCleanupRef.current = () => {
                map.off('click', onMapClick);
                map.off('contextmenu', onRightClick);
                map.off('mousemove', onMouseMove);
                removeDrawingLayer();
            };

        } else if (activeTool === 'cut') {

            const onCuttingMapClick = (e) => {
                const coords = [e.lngLat.lng, e.lngLat.lat];
                if (!isCuttingRef.current) {
                    isCuttingRef.current = true;
                    cutLineCoordsRef.current = [coords];
                } else {
                    cutLineCoordsRef.current.push(coords);
                    performCut();
                    isCuttingRef.current = false;
                    cutLineCoordsRef.current = [];
                    resetCutting();
                    updateCuttingLayer([]);
                }
                updateCuttingLayer(cutLineCoordsRef.current);
            };

            const onCuttingMouseMove = (e) => {
                if (isCuttingRef.current && cutLineCoordsRef.current.length > 0) {
                    const coords = [e.lngLat.lng, e.lngLat.lat];
                    const currentLine = [...cutLineCoordsRef.current, coords];
                    updateCuttingLayer(currentLine);
                    autoPanMapFunction(e);
                    autoPanMap(e);
                }
            };

            const performCut = () => {
                if (cutLineCoordsRef.current.length < 2) return;
                const cutLineString = turfLineString(cutLineCoordsRef.current);
                const newLines = [];
                let cutSuccessful = false;
                const originalLines = [...lines];
                for (const line of lines) {
                    const latlngs = line.latlngs;
                    if (!Array.isArray(latlngs) || latlngs.length < 2) {
                        newLines.push(line);
                        continue;
                    }
                    const lineString = turfLineString(latlngs.map((coord) => [coord.lng, coord.lat]));
                    const intersections = turfLineIntersect(lineString, cutLineString);
                    if (intersections.features.length > 0) {
                        cutSuccessful = true;
                        const splitResult = turfLineSplit(lineString, cutLineString);
                        for (const f of splitResult.features) {
                            const newLineCoordsArr = f.geometry.coordinates.map((coord) => ({ lat: coord[1], lng: coord[0] }));
                            const newLineObj = {
                                id: nextLineIdRef.current++,
                                latlngs: newLineCoordsArr,
                                properties: { ...line.properties, lineId: nextLineIdRef.current - 1 },
                            };
                            newLines.push(newLineObj);
                        }
                    } else {
                        newLines.push(line);
                    }
                }

                if (cutSuccessful) {
                    setLines(newLines);
                    setActionHistory((prevHistory) => [...prevHistory, { type: 'cut', originalLines }]);
                }
            };

            const updateCuttingLayer = (coordinates) => {
                safeMapCall((m) => {
                    const source = m.getSource('cutting-line');
                    if (source && source.setData) {
                        source.setData({
                            type: 'Feature',
                            geometry: { type: 'LineString', coordinates: coordinates },
                        });
                    } else {
                        if (!m.getSource('cutting-line')) {
                            m.addSource('cutting-line', {
                                type: 'geojson',
                                data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coordinates } },
                            });
                        }

                        if (!m.getLayer('cutting-line-layer')) {
                            m.addLayer({
                                id: 'cutting-line-layer',
                                type: 'line',
                                source: 'cutting-line',
                                paint: { 'line-color': '#0000ff', 'line-width': 2, 'line-dasharray': [2, 4] },
                            });
                        }
                    }
                });
            };

            map.on('click', onCuttingMapClick);
            map.on('mousemove', onCuttingMouseMove);

            currentToolCleanupRef.current = () => {
                map.off('click', onCuttingMapClick);
                map.off('mousemove', onCuttingMouseMove);
                removeCuttingLayer();
            };

        } else if (activeTool === 'measurement') {
            // === NUEVO: herramienta de medición ===
            const onMeasureClick = (e) => {
                const coord = [e.lngLat.lng, e.lngLat.lat];
                if (!isMeasuringRef.current) {
                    isMeasuringRef.current = true;
                    measureCoordsRef.current = [coord];
                } else {
                    measureCoordsRef.current.push(coord);
                }
                updateMeasurementLayers(measureCoordsRef.current);
            };

            const onMeasureMove = (e) => {
                if (!isMeasuringRef.current || measureCoordsRef.current.length === 0) return;
                const temp = [...measureCoordsRef.current, [e.lngLat.lng, e.lngLat.lat]];
                updateMeasurementLayers(temp);
                autoPanMapFunction(e);
                autoPanMap(e);
            };

            const onMeasureFinish = (e) => {
                // botón derecho para finalizar y mantener última lectura en pantalla
                e.preventDefault();
                if (!isMeasuringRef.current) return;
                isMeasuringRef.current = false;
                updateMeasurementLayers(measureCoordsRef.current);
            };

            map.on('click', onMeasureClick);
            map.on('mousemove', onMeasureMove);
            map.on('contextmenu', onMeasureFinish);

            currentToolCleanupRef.current = () => {
                map.off('click', onMeasureClick);
                map.off('mousemove', onMeasureMove);
                map.off('contextmenu', onMeasureFinish);
                // Nota: mantenemos la última medición si no se cambió de herramienta;
                // si deseas limpiar al salir, descomenta:
                // removeMeasurementLayers();
            };

        }  else if(activeTool === 'stretch'){
            currentToolCleanupRef.current = setupStretchMode();

        }else {
            if (currentToolCleanupRef.current) {
                currentToolCleanupRef.current();
                currentToolCleanupRef.current = null;
            }
            safeMapCall((m) => {
                if (m.getLayer('drawing-line-layer')) m.removeLayer('drawing-line-layer');
                if (m.getSource('drawing-line')) m.removeSource('drawing-line');
                if (m.getLayer('cutting-line-layer')) m.removeLayer('cutting-line-layer');
                if (m.getSource('cutting-line')) m.removeSource('cutting-line');
                removeMeasurementLayers();
            });
            isDrawingRef.current = false;
            newLineCoordsRef.current = [];
            isCuttingRef.current = false;
            cutLineCoordsRef.current = [];
            isMeasuringRef.current = false;
            measureCoordsRef.current = [];
        }
    }, [
        activeTool,
        mapRef,
        autoPanMapFunction,
        safeMapCall,
        resetDrawing,
        resetCutting,
        removeDrawingLayer,
        removeCuttingLayer,
        updateMeasurementLayers,
        removeMeasurementLayers,
        setupStretchMode
    ]);

    const autoPanMap = useCallback((e) => {
        const map = mapRef.current;
        if (!map) return;

        const canvas = map.getCanvas();
        const rect = canvas.getBoundingClientRect();
        const edgeThreshold = 50;

        const mouseX = e.point.x;
        const mouseY = e.point.y;

        const panOffset = { x: 0, y: 0 };

        if (mouseX < edgeThreshold) panOffset.x = -50;
        else if (mouseX > rect.width - edgeThreshold) panOffset.x = 50;

        if (mouseY < edgeThreshold) panOffset.y = -50;
        else if (mouseY > rect.height - edgeThreshold) panOffset.y = 50;

        if (panOffset.x !== 0 || panOffset.y !== 0) {
            map.panBy([panOffset.x, panOffset.y], { duration: 100 });
        }
    }, [mapRef]);

    useEffect(() => { applyFilters(); }, [
        filterSpeed, filterAltitude, filterRealDose,
        lowSpeed, medSpeed, highSpeed,
        lowAltitude, medAltitude, highAltitude,
        lowRealDose, medRealDose, highRealDose,
        applyFilters
    ]);

    useEffect(() => { activeToolRef.current = activeTool; }, [activeTool]);

    const handleLabelClick = useCallback(
        (text, color) => { if (typeof setLabelsAndColors === 'function') setLabelsAndColors({ text, color }); },
        [setLabelsAndColors]
    );

    useEffect(() => {
        if (isFilteringLines && lines.length > 0 && activarEdicionInteractiva) {
            setActiveFilter("aerialApplicationsTraslapeDrones");
            setIsFilteringLines(false);
        }
    }, [isFilteringLines, lines, activarEdicionInteractiva]);

    // isEditing: sólo para draw/cut
    const isEditing =
        activeTool === 'draw' ||
        activeTool === 'cut'  ||
        activeTool === 'stretch';

    const hasRemoteTasaVariableLayers = Boolean(
        isTasaVariableMap &&
        tasaVariableRemoteLayers &&
        (
            tasaVariableRemoteLayers.lineasAplicacion ||
            tasaVariableRemoteLayers.lineasAplicadasEnMaleza ||
            tasaVariableRemoteLayers.lineasAplicadasFueraMaleza ||
            tasaVariableRemoteLayers.malezaObjetivo ||
            tasaVariableRemoteLayers.malezaNoAplicada
        )
    );

    /*
     * Estilos exclusivos del análisis VAR de aplicaciones aéreas.
     *
     * CommonMap es compartido por otros análisis, por lo que no se modifica su
     * implementación global. Cuando VAR está activo, este efecto usa la
     * instancia existente de Mapbox para:
     *   - colocar las superficies de maleza por encima del lote amarillo;
     *   - reforzar sus colores y opacidades;
     *   - añadir contornos específicos del modo VAR;
     *   - mantener las líneas clasificadas por encima de los polígonos.
     *
     * El estilo del lote amarillo (polygons-data-layer) NO se toca: antes se
     * bajaba su opacidad a 0.055 en cada 'idle', y el color desaparecía en
     * cuanto el usuario movía o hacía zoom en el mapa.
     *
     * Al salir del análisis VAR se eliminan únicamente los contornos añadidos
     * por este componente.
     */
    useEffect(() => {
        const shouldApplyVraPresentation = isVraModeSelected || isTasaVariableMap;
        if (!shouldApplyVraPresentation) {
            return undefined;
        }

        const VAR_OBJECTIVE_OUTLINE_ID = 'var-aerial-maleza-objetivo-outline-layer';
        const VAR_PENDING_OUTLINE_ID = 'var-aerial-maleza-no-aplicada-outline-layer';
        const MAX_RETRIES = 40;
        let retryCount = 0;
        let retryTimer = null;
        let cancelled = false;

        const getMap = () => {
            const ref = mapRef?.current || null;
            if (!ref) return null;
            return typeof ref.getMap === 'function' ? ref.getMap() : ref;
        };

        const stringify = (value) => {
            try {
                return JSON.stringify(value);
            } catch (error) {
                return String(value);
            }
        };

        const setPaintIfNeeded = (map, layerId, property, value) => {
            if (!map.getLayer(layerId)) return;
            try {
                const currentValue = map.getPaintProperty(layerId, property);
                if (stringify(currentValue) !== stringify(value)) {
                    map.setPaintProperty(layerId, property, value);
                }
            } catch (error) {
                console.warn(`[VAR map] No se pudo configurar ${layerId}.${property}:`, error);
            }
        };

        const ensureOutlineLayer = (map, { id, source, color, width, opacity }) => {
            if (!map.getSource(source) || map.getLayer(id)) return;
            try {
                map.addLayer({
                    id,
                    type: 'line',
                    source,
                    paint: {
                        'line-color': color,
                        'line-width': width,
                        'line-opacity': opacity,
                    },
                });
            } catch (error) {
                // La fuente puede estar recreándose al cambiar de overview a
                // detalle. El siguiente reintento volverá a añadir el contorno.
                console.debug(`[VAR map] Contorno ${id} aún no disponible.`, error);
            }
        };

        const moveVarLayersAboveLote = (map) => {
            const desiredOrder = [
                'var-maleza-objetivo-layer',
                VAR_OBJECTIVE_OUTLINE_ID,
                'var-maleza-no-aplicada-layer',
                VAR_PENDING_OUTLINE_ID,
                'var-aplicacion-poligonos-layer',
                'var-aplicacion-poligonos-outline-layer',
                'lines-layer',
            ].filter((layerId) => map.getLayer(layerId));

            if (desiredOrder.length === 0) return false;

            const styleLayers = map.getStyle()?.layers || [];
            const layerIndex = new Map(styleLayers.map((layer, index) => [layer.id, index]));
            const loteIndex = layerIndex.get('polygons-data-layer');
            const positions = desiredOrder.map((layerId) => layerIndex.get(layerId));
            const allPresent = positions.every((position) => Number.isInteger(position));
            const alreadyOrdered = allPresent && positions.every(
                (position, index) => index === 0 || position > positions[index - 1]
            );
            const alreadyAboveLote = !Number.isInteger(loteIndex) || positions.every(
                (position) => Number.isInteger(position) && position > loteIndex
            );

            if (alreadyOrdered && alreadyAboveLote) return true;

            desiredOrder.forEach((layerId) => {
                try {
                    map.moveLayer(layerId);
                } catch (error) {
                    console.debug(`[VAR map] La capa ${layerId} todavía no puede moverse.`, error);
                }
            });
            return true;
        };

        const applyVarPolygonPresentation = () => {
            const map = getMap();
            if (!map || typeof map.getStyle !== 'function') return false;
            if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) return false;

            setPaintIfNeeded(map, 'var-maleza-objetivo-layer', 'fill-color', '#00ACC1');
            setPaintIfNeeded(map, 'var-maleza-objetivo-layer', 'fill-opacity', 0.60);
            setPaintIfNeeded(map, 'var-maleza-objetivo-layer', 'fill-outline-color', '#006064');
            setPaintIfNeeded(map, 'var-maleza-no-aplicada-layer', 'fill-color', '#7E57C2');
            setPaintIfNeeded(map, 'var-maleza-no-aplicada-layer', 'fill-opacity', 0.90);
            setPaintIfNeeded(map, 'var-maleza-no-aplicada-layer', 'fill-outline-color', '#4527A0');

            ensureOutlineLayer(map, {
                id: VAR_OBJECTIVE_OUTLINE_ID,
                source: 'var-maleza-objetivo-remote',
                color: '#006064',
                width: ['interpolate', ['linear'], ['zoom'], 12, 0.7, 16, 1.2, 18, 1.6],
                opacity: 0.94,
            });
            ensureOutlineLayer(map, {
                id: VAR_PENDING_OUTLINE_ID,
                source: 'var-maleza-no-aplicada-remote',
                color: '#4527A0',
                width: ['interpolate', ['linear'], ['zoom'], 12, 0.8, 16, 1.35, 18, 1.8],
                opacity: 0.98,
            });

            const movedRemoteLayers = moveVarLayersAboveLote(map);
            return movedRemoteLayers || Boolean(map.getLayer('polygons-data-layer'));
        };

        const scheduleApply = (delay = 0) => {
            if (cancelled) return;
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(() => {
                if (cancelled) return;
                const applied = applyVarPolygonPresentation();
                if (!applied && retryCount < MAX_RETRIES) {
                    retryCount += 1;
                    scheduleApply(250);
                }
            }, delay);
        };

        const handleStyleData = () => scheduleApply(40);
        const handleSourceData = () => scheduleApply(40);
        const handleIdle = () => scheduleApply(0);
        const map = getMap();
        if (map && typeof map.on === 'function') {
            map.on('styledata', handleStyleData);
            map.on('sourcedata', handleSourceData);
            map.on('idle', handleIdle);
        }
        scheduleApply();

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);

            const currentMap = getMap();
            if (!currentMap) return;
            if (typeof currentMap.off === 'function') {
                currentMap.off('styledata', handleStyleData);
                currentMap.off('sourcedata', handleSourceData);
                currentMap.off('idle', handleIdle);
            }
            [VAR_OBJECTIVE_OUTLINE_ID, VAR_PENDING_OUTLINE_ID].forEach((layerId) => {
                try {
                    if (currentMap.getLayer(layerId)) currentMap.removeLayer(layerId);
                } catch (error) {
                    // El style puede estar desmontándose; no requiere acción.
                }
            });
        };
    }, [hasRemoteTasaVariableLayers, isTasaVariableMap, isVraModeSelected, mapRef, tasaVariableRemoteLayers]);

    return (
        <>
            <CommonMap
                center={mapCenter}
                zoom={zoom}
                polygons={polygons}
                setPolygons={setPolygons}
                lines={lines}
                points={varPoints}
                hullPolygon={null}
                nonIntersectedAreas={nonIntersectedAreas}
                bufferedLines={bufferedLines}
                bufferedIntersections={bufferedIntersections}
                onLineHover={() => {}}
                onLineMouseOut={() => {}}
                onLineClick={handleLineClick}
                polygonProperties={polygonsProperties}
                showIntersections={!isTasaVariableMap}
                isTasaVariableMap={isTasaVariableMap}
                tasaVariableRemoteLayers={tasaVariableRemoteLayers}
                tasaVariableBounds={tasaVariableBounds}
                mapRef={mapRef}
                userId={userData.userId || null}
                stretchPoints={[]}
                lineasNoFiltradas={showUnfilteredLines && !isTasaVariableMap ? lineasNoFiltradas : []}
                highlightedLote={highlightedLote}
                polygonsData={polygonsData}
                visibleLotePolygons={stableVisibleLotePolygons}
                clipPolygonsFC={hasToolbarLoteFilters ? stableVisibleLotePolygons : undefined}
                activeLotes={activeLotes}
                onLeaveLote={onLeaveLote}
                onSelectLote={onSelectLote}
                onHoverLote={onHoverLote}
                areasSuperpuestas={areasSuperpuestas}
                setImgLaflet={setImgLaflet}
                polygonsLayerRef={polygonsLayerRef}
                setLines={setLines}
                popupInfo={popupInfo}
                setPopupInfo={setPopupInfo}
                isEditing={isEditing}
                setNorthWestCoords={setNorthWestCoords}
                setSouthEastCoords={setSouthEastCoords}     // <-- corregido
                setCoordinateSystem={setCoordinateSystem}    // <-- corregido
                onClippedBufferChange={handleClippedBufferChange}
            />

            {((Array.isArray(polygons) && polygons.length > 0) || (Array.isArray(lines) && lines.length > 0) || hasRemoteTasaVariableLayers) && (
                <BarIndicator
                    filterType={activeFilter ? activeFilter : 'aerialApplicationsTraslape'}
                    isHistory={false}
                    onLabelClick={handleLabelClick}
                    setLabelsAndColors={setLabelsAndColors}
                />
            )}

            <MapDialog
                isOpen={!!isFilterDialogOpen}
                onClose={() => { if (typeof closeFilterDialog === 'function') closeFilterDialog(); }}
                availableFilters={availableFilters}
                handleToggleFilter={(fName) => {
                    switch (fName) {
                        case 'VELOCIDAD':
                            setFilterSpeed(prev => {
                                const next = !prev;
                                if (next) { setFilterAltitude(false); setFilterRealDose(false); }
                                return next;
                            });
                            break;
                        case 'ALTURA':
                            setFilterAltitude(prev => {
                                const next = !prev;
                                if (next) { setFilterSpeed(false); setFilterRealDose(false); }
                                return next;
                            });
                            break;
                        case 'DOSISREAL':
                            setFilterRealDose(prev => {
                                const next = !prev;
                                if (next) { setFilterSpeed(false); setFilterAltitude(false); }
                                return next;
                            });
                            break;
                        default:
                            break;
                    }
                }}
                filterSpeed={filterSpeed}
                filterAltitude={filterAltitude}
                filterRealDose={filterRealDose}
                lowSpeed={lowSpeed}
                medSpeed={medSpeed}
                highSpeed={highSpeed}
                setLowSpeed={setLowSpeed}
                setMedSpeed={setMedSpeed}
                setHighSpeed={setHighSpeed}
                lowAltitude={lowAltitude}
                medAltitude={setMedAltitude}
                highAltitude={highAltitude}
                setLowAltitude={setLowAltitude}
                setMedAltitude={setMedAltitude}
                setHighAltitude={setHighAltitude}
                lowRealDose={lowRealDose}
                medRealDose={medRealDose}
                highRealDose={highRealDose}
                setLowRealDose={setLowRealDose}
                setMedRealDose={setMedRealDose}
                setHighRealDose={setHighRealDose}
                setTipoMapeo={setTipoMapeo}
            />

            <Snackbar
                open={!isTasaVariableMap && !areasCalculated && Array.isArray(lines) && lines.length > 0 && polygons.length === 0}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert severity="warning" variant="filled" sx={{ width: '100%' }}>
                    <strong>Advertencia:</strong> Genera el buffer en las líneas para mostrar las áreas en el dashboard.
                </Alert>
            </Snackbar>
        </>
    );
};

export default AerialApplications;
